import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import {
	initializeFirestore,
	persistentLocalCache,
	persistentMultipleTabManager,
	doc,
	getDoc,
	setDoc,
	updateDoc,
	getDocs,
	query,
	collection,
	where,
	getCountFromServer,
	or,
	and,
	orderBy,
	limit,
	onSnapshot,
	addDoc,
	arrayUnion,
	arrayRemove,
	deleteDoc,
	writeBatch,
	collectionGroup,
	serverTimestamp,
	Timestamp,
} from 'firebase/firestore';
import type { DocumentData, DocumentReference, Firestore, QuerySnapshot, UpdateData, WriteBatch } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { v4 as uuid } from 'uuid';
import { collections, ApplicationStatus, ApplicationType, InterviewStatus, applicationSpecificCollections, SearchableCollections } from './collections';
import type { ApplicationStatusValue, ApplicationTypeValue, CollectionKey, UserTypeValue } from './collections';
import { UAParser } from 'ua-parser-js';
import axios from 'axios';
import { resolveEffectiveCycleYear } from '../../utils/cycleYear';
import type {
	CollectionName,
	InterviewRecord,
	MeetingRecord,
	PermanentlyDeleteApplicationParams,
	PurgeUserRecordsParams,
	RealtimeCallback,
} from '../../types/firebase';

type WipeCollectionsInput = { conn?: string } | Firestore | null | undefined;

type InterviewICSInput = {
	id: string;
	data?: () => Record<string, unknown>;
	startTime?: unknown;
	endTime?: unknown;
	title?: unknown;
	description?: unknown;
};

const firestoreValueToDate = (value: unknown): Date => {
	if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
		return value.toDate();
	}
	return new Date(value as string | number | Date);
};

const getInterviewICSFields = (interview: InterviewICSInput): Record<string, unknown> => {
	if (typeof interview.data === 'function') {
		return interview.data();
	}
	return {
		startTime: interview.startTime,
		endTime: interview.endTime,
		title: interview.title,
		description: interview.description,
	};
};

const isApplicationTypeValue = (value: string): value is ApplicationTypeValue =>
	(Object.values(ApplicationType) as string[]).includes(value);

const isApplicationStatusValue = (value: string): value is ApplicationStatusValue =>
	(Object.values(ApplicationStatus) as string[]).includes(value);

const siteConfigString = (config: Record<string, unknown> | null, key: string): string => {
	const value = config?.[key];
	return typeof value === 'string' ? value : '';
};

const siteConfigCycleYear = (config: Record<string, unknown> | null): number =>
	resolveEffectiveCycleYear(config);

const PIPELINE_STATUSES: ApplicationStatusValue[] = [
	ApplicationStatus.completed,
	ApplicationStatus.eligible,
	ApplicationStatus.invited,
	ApplicationStatus.deferred,
	ApplicationStatus.awarded,
	ApplicationStatus.denied,
];

const NEGATABLE_STATUSES = new Set<string>([
	ApplicationStatus.deleted,
	ApplicationStatus.ineligible,
	ApplicationStatus.deferred,
]);

const isRecentApplicationVisible = (status: unknown): boolean => !NEGATABLE_STATUSES.has(String(status ?? ''));

const applicationActivityTime = (data: DocumentData): number => {
	for (const key of ['lastUpdated', 'submittedOn', 'dated'] as const) {
		const value = data[key];
		if (value === null || value === undefined || value === '') continue;
		const time = firestoreValueToDate(value).getTime();
		if (!Number.isNaN(time)) return time;
	}
	return 0;
};

const sortApplicationsByLastTouched = <T extends DocumentData>(apps: T[]): T[] =>
	[...apps].sort((a, b) => applicationActivityTime(b) - applicationActivityTime(a));

const recentApplicationIdsFromDocs = (
	docs: { id: string; data: () => DocumentData }[],
	limitCount: number,
	cycleYear: number
): string[] =>
	[...docs]
		.filter((doc) => {
			const data = doc.data();
			if (!isRecentApplicationVisible(data.status)) return false;
			return resolveApplicationCycleYear(data) === cycleYear;
		})
		.sort((a, b) => applicationActivityTime(b.data()) - applicationActivityTime(a.data()))
		.slice(0, limitCount)
		.map((doc) => doc.id);

export const resolveApplicationCycleYear = (app: DocumentData): number | null => {
	if (typeof app.cycleYear === 'number' && !Number.isNaN(app.cycleYear)) return app.cycleYear;
	if (typeof app.cycleYear === 'string' && /^\d{4}$/.test(app.cycleYear.trim())) {
		return Number(app.cycleYear.trim());
	}
	if (app.window) {
		const year = new Date(String(app.window)).getFullYear();
		return Number.isNaN(year) ? null : year;
	}
	return null;
};

export const resolveInterviewCycleYear = (interview: DocumentData): number | null => {
	if (typeof interview.cycleYear === 'number' && !Number.isNaN(interview.cycleYear)) return interview.cycleYear;
	if (typeof interview.cycleYear === 'string' && /^\d{4}$/.test(interview.cycleYear.trim())) {
		return Number(interview.cycleYear.trim());
	}
	const deadline = interview.deadline ?? interview.window;
	if (deadline) {
		const year = new Date(String(deadline)).getFullYear();
		return Number.isNaN(year) ? null : year;
	}
	return null;
};

const interviewStartTime = (interview: DocumentData): number => {
	const value = interview.startTime;
	if (value === null || value === undefined) return 0;
	const time = firestoreValueToDate(value).getTime();
	return Number.isNaN(time) ? 0 : time;
};

/** True when gradYear is stored as a plain 4-digit year (number or string). */
export const isCanonicalGradYear = (gradYear: unknown): boolean => {
	if (typeof gradYear === 'number' && !Number.isNaN(gradYear)) return gradYear >= 1900 && gradYear <= 2100;
	if (typeof gradYear === 'string' && /^\d{4}$/.test(gradYear.trim())) return true;
	return false;
};

/** Normalize any legacy gradYear shape to a 4-digit graduation year. */
export const normalizeGradYear = (gradYear: unknown): number | null => {
	if (gradYear === null || gradYear === undefined) return null;

	if (typeof gradYear === 'number' && !Number.isNaN(gradYear)) {
		return gradYear >= 1900 && gradYear <= 2100 ? gradYear : null;
	}

	if (typeof gradYear === 'string') {
		const trimmed = gradYear.trim();
		if (!trimmed) return null;
		if (/^\d{4}$/.test(trimmed)) return Number(trimmed);
		const parsed = new Date(trimmed);
		if (!Number.isNaN(parsed.getTime())) return parsed.getFullYear();
		return null;
	}

	if (gradYear && typeof gradYear === 'object') {
		if ('toDate' in gradYear && typeof gradYear.toDate === 'function') {
			const parsed = gradYear.toDate();
			return Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
		}
		if ('seconds' in gradYear && typeof gradYear.seconds === 'number') {
			return new Date(gradYear.seconds * 1000).getFullYear();
		}
	}

	return null;
};

const parseGradYear = (gradYear: unknown): number | null => normalizeGradYear(gradYear);

const gradYearFromEducationRecord = (education: DocumentData | null | undefined): number | null => {
	if (!education?.expectedGraduationDate) return null;
	return normalizeGradYear(education.expectedGraduationDate);
};

const normalizeApplicantGradYearField = (data: Record<string, unknown>): Record<string, unknown> => {
	if (!('gradYear' in data)) return data;
	const normalized = normalizeGradYear(data.gradYear);
	return { ...data, gradYear: normalized };
};

const applicantHasNotGraduated = (gradYear: unknown, cycleYear: number): boolean => {
	const parsed = parseGradYear(gradYear);
	if (parsed === null) return true;
	return parsed >= cycleYear;
};

export interface AwardTrendYear {
	year: number;
	New: number;
	Returning: number;
	Scholarship: number;
}

export interface DashboardBenchmarkData {
	currentCounts: Record<ApplicationTypeValue, number>;
	benchmarkTargets: Record<ApplicationTypeValue, number>;
	awardTrends: AwardTrendYear[];
}

export type OutlookSectionId =
	| 'brandNewAccounts'
	| 'expectedNewApplications'
	| 'lostInTheWeeds'
	| 'expectedReturningGrants'
	| 'expectedReturningScholarships';

export type OutlookRowCycleStatus = 'none' | 'correct' | 'wrong';

export interface DashboardApplicantOutlookRow {
	applicantId: string;
	name: string;
	gradYear: string | null;
	awardDisplay: string;
	lastAwardType: string | null;
	lastCycleYear: number | null;
	cycleStatus: OutlookRowCycleStatus;
}

export interface DashboardApplicantOutlookSection {
	id: OutlookSectionId;
	title: string;
	link: string;
	expectedType: ApplicationTypeValue | null;
	applicants: DashboardApplicantOutlookRow[];
	isComplete: boolean;
}

export interface DashboardApplicantOutlook {
	cycleYear: number;
	sections: DashboardApplicantOutlookSection[];
}

const MAX_UNDERGRAD_AWARD_CYCLES = 4;

const SUBMITTED_OR_BEYOND_STATUSES = new Set<string>([
	ApplicationStatus.submitted,
	ApplicationStatus.completed,
	ApplicationStatus.eligible,
	ApplicationStatus.invited,
	ApplicationStatus.deferred,
	ApplicationStatus.awarded,
	ApplicationStatus.denied,
]);

const OUTLOOK_PIPELINE_SECTIONS: {
	id: OutlookSectionId;
	title: string;
	pathKey: 'applicants' | 'newAppsInYear' | 'returningAppsInYear' | 'scholarshipAppsInYear';
	expectedType: ApplicationTypeValue | null;
}[] = [
	{ id: 'brandNewAccounts', title: 'Brand New Accounts', pathKey: 'applicants', expectedType: null },
	{ id: 'expectedNewApplications', title: 'Expected New Applicant Applications', pathKey: 'newAppsInYear', expectedType: ApplicationType.newApplication },
	{ id: 'expectedReturningGrants', title: 'Expected Returning Grants', pathKey: 'returningAppsInYear', expectedType: ApplicationType.returningGrant },
	{ id: 'expectedReturningScholarships', title: 'Expected Returning Scholarships', pathKey: 'scholarshipAppsInYear', expectedType: ApplicationType.scholarship },
	{ id: 'lostInTheWeeds', title: 'Lost in the Weeds', pathKey: 'applicants', expectedType: ApplicationType.newApplication },
];

const emptyTypeCounts = (): Record<ApplicationTypeValue, number> => ({
	[ApplicationType.newApplication]: 0,
	[ApplicationType.returningGrant]: 0,
	[ApplicationType.scholarship]: 0,
});

const emptyAwardTrend = (year: number): AwardTrendYear => ({
	year,
	New: 0,
	Returning: 0,
	Scholarship: 0,
});

const incrementAwardTrend = (trends: Record<number, AwardTrendYear>, year: number, type: string) => {
	if (!trends[year]) trends[year] = emptyAwardTrend(year);
	if (type === ApplicationType.newApplication) trends[year].New++;
	else if (type === ApplicationType.returningGrant) trends[year].Returning++;
	else if (type === ApplicationType.scholarship) trends[year].Scholarship++;
};

let firebaseConfig = {
	apiKey: process.env.REACT_APP_apiKey,
	authDomain: process.env.REACT_APP_authDomain,
	projectId: process.env.REACT_APP_projectId,
	storageBucket: process.env.REACT_APP_storageBucket,
	messagingSenderId: process.env.REACT_APP_messagingSenderId,
	appId: process.env.REACT_APP_appId,
	measurementId: process.env.REACT_APP_measurementId,
};

const app = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

const prodDB = initializeFirestore(app, {
	localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
const testDB = initializeFirestore(
	app,
	{
		localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
	},
	'pf-test'
);
const environment = process.env.REACT_APP_environment;
const useTestDB = environment !== 'production';
const CONFIG_DOC_ID = process.env.REACT_APP_configKey ?? '';
export const db = useTestDB ? testDB : prodDB;

export const changeUserEmail = httpsCallable(functions, 'changeUserEmail');
export const backfillLastUpdated = httpsCallable(functions, 'backfillLastUpdated');
export const backfillSentEmailTags = httpsCallable(functions, 'backfillSentEmailTags');
export const backfillSearchableTerms = httpsCallable(functions, 'backfillSearchableTerms');
export const backfillEmailContent = httpsCallable(functions, 'backfillEmailContent');
export const sendZohoEmail = httpsCallable(functions, 'sendZohoEmail');
export const updateEmailReadStatus = httpsCallable(functions, 'updateEmailReadStatus');
export const deleteZohoEmail = httpsCallable(functions, 'deleteZohoEmail');
export const fetchAttachmentContent = httpsCallable(functions, 'fetchAttachmentContent');
export const fetchEmailContent = httpsCallable(functions, 'fetchEmailContent');
export const bulkDeleteZohoEmails = httpsCallable(functions, 'bulkDeleteZohoEmails');
export const getUserAuthRecord = httpsCallable(functions, 'getUserAuthRecord');
export const getApplicantsByEmail = httpsCallable(functions, 'getApplicantsByEmail');
export const getApplicantsForMerge = httpsCallable(functions, 'getApplicantsForMerge');
export const mergeApplicantAccounts = httpsCallable(functions, 'mergeApplicantAccounts');
export const getRoomDetails = httpsCallable(functions, 'getRoomDetails');
export const markInterviewAsMissed = httpsCallable(functions, 'markInterviewAsMissed');
export const endInterview = httpsCallable(functions, 'endInterview');
export const autoScheduleInterviews = httpsCallable(functions, 'autoScheduleInterviews');
export const scheduleSingleInterview = httpsCallable(functions, 'scheduleSingleInterview');
export const rescheduleInterview = httpsCallable(functions, 'rescheduleInterview');
export const globalSearch = httpsCallable(functions, 'globalSearch');
export const deleteSingleInterview = httpsCallable(functions, 'deleteSingleInterview');
export const createInterviewRoom = httpsCallable(functions, 'createInterviewRoom');
export const closeInterviewRoom = httpsCallable(functions, 'closeInterviewRoom');
export const updateInterviewStatus = httpsCallable(functions, 'updateInterviewStatus');
export const sendInterviewInvitations = httpsCallable(functions, 'sendInterviewInvitations');
export const bulkDeleteInterviews = httpsCallable(functions, 'bulkDeleteInterviews');
export const deleteDeliberationRoom = httpsCallable(functions, 'deleteDeliberationRoom');
export const createDeliberationRoom = httpsCallable(functions, 'createDeliberationRoom');
export const bulkUpdateInterviewStatus = httpsCallable(functions, 'bulkUpdateInterviewStatus');
export const generateJoinToken = httpsCallable(functions, 'generateJoinToken');
export const resolveRecommendationUpload = httpsCallable(functions, 'resolveRecommendationUpload');
export const verifyRecommendationUploadPin = httpsCallable(functions, 'verifyRecommendationUploadPin');
export const completeRecommendationUpload = httpsCallable(functions, 'completeRecommendationUpload');
export const generateRecommendationUploadLink = httpsCallable(functions, 'generateRecommendationUploadLink');
export const hashRecommendationPin = httpsCallable(functions, 'hashRecommendationPin');
export const generateUnsubscribeLinkCallable = httpsCallable(functions, 'generateUnsubscribeLink');
export const resolveUnsubscribeToken = httpsCallable(functions, 'resolveUnsubscribeToken');
export const recordSignedRsvp = httpsCallable(functions, 'recordSignedRsvp');
export const updateMemberPermissions = httpsCallable(functions, 'updateMemberPermissions');
export const setSiteSecrets = httpsCallable(functions, 'setSiteSecrets');
export const migrateSiteSecrets = httpsCallable(functions, 'migrateSiteSecrets');
export const permanentlyDeleteApplicationCallable = httpsCallable(functions, 'permanentlyDeleteApplication');
export const purgeDeletedApplications = httpsCallable(functions, 'purgeDeletedApplications');
export const findUnownedRecords = httpsCallable(functions, 'findUnownedRecords');
export const purgeUnownedRecords = httpsCallable(functions, 'purgeUnownedRecords');
export const cleanupOrphanedStorage = httpsCallable(functions, 'cleanupOrphanedStorage');
export const purgeLogs = httpsCallable(functions, 'purgeLogs');
export const submitPublicContact = httpsCallable(functions, 'submitPublicContact');
export const acknowledgePasswordReset = httpsCallable(functions, 'acknowledgePasswordReset');
export const deleteMyApplicantAccount = httpsCallable(functions, 'deleteMyApplicantAccount');

export const sendToTestDB = async () => {
	try {
		for (const key of Object.keys(collections) as CollectionKey[]) {
			const collectionName = collections[key as CollectionKey];

			const defaultCollectionRef = collection(prodDB, collectionName);
			const testCollectionRef = collection(testDB, collectionName);

			const snapshot = await getDocs(defaultCollectionRef);

			const copyPromises = snapshot.docs.map(async (docSnapshot) => {
				const docData = docSnapshot.data();
				const docRef = doc(testCollectionRef, docSnapshot.id);
				await setDoc(docRef, docData);
			});

			await Promise.all(copyPromises);
		}

		alert('Database copied successfully!');
	} catch (error) {
		console.error('Error copying database:', error);
		alert('Error copying database.');
	}
};

export const migrateEmailTemplates = async () => {
	try {
		const { emailTemplates } = await import('../content/emailTemplates');
		for (const [key, templateData] of Object.entries(emailTemplates)) {
			const newRef = doc(db, collections.emailTemplates, key);
			await setDoc(newRef, { ...templateData, key, isSystem: true });
		}
		// Remove renamed/deprecated system keys so Contact Center does not show stale buttons
		const deprecatedKeys = ['interviewDayLocked'];
		for (const key of deprecatedKeys) {
			if (!(key in emailTemplates)) {
				await deleteDoc(doc(db, collections.emailTemplates, key)).catch(() => undefined);
			}
		}
		alert('Email templates migrated successfully!');
	} catch (error) {
		console.error('Error migrating email templates:', error);
		alert('Error migrating email templates.');
	}
};

const deleteAllDocsInCollection = async (collectionName: CollectionName, conn: Firestore = testDB) => {
	const querySnapshot = await getDocs(collection(conn, collectionName));
	const deletePromises = querySnapshot.docs.map((docSnapshot) => deleteDoc(doc(conn, collectionName, docSnapshot.id)));
	await Promise.all(deletePromises);
	console.log(`All documents in ${collectionName} deleted successfully.`);
	return true;
};

const clearApplicantsApplications = async (conn = testDB) => {
	const querySnapshot = await getDocs(collection(conn, collections.applicants));
	const updatePromises = querySnapshot.docs.map((docSnapshot) => updateDoc(doc(conn, collections.applicants, docSnapshot.id), { applications: [], awards: [] }));
	await Promise.all(updatePromises);
	return true;
};

export const wipeCollections = async (input: WipeCollectionsInput = null) => {
	let targetDB = testDB;

	if (input && typeof input === 'object' && 'conn' in input && input.conn) {
		if (input.conn === '(default)') {
			targetDB = prodDB;
		} else if (input.conn === 'pf-test') {
			targetDB = testDB;
		}
	} else if (input && typeof input === 'object' && !('conn' in input)) {
		targetDB = input as Firestore;
	}

	const connLabel = input && typeof input === 'object' && 'conn' in input ? input.conn : 'direct-instance';
	console.log(`Wiping collection using database: ${connLabel}`);

	// Purposely omitted collections: collections.applicants, collections.members, collections.users, collections.siteConfig
	const collectionNames = [
		collections.applications,
		collections.attachments,
		collections.contributions,
		collections.education,
		collections.emails,
		collections.expenses,
		collections.families,
		collections.incomes,
		collections.profiles,
		collections.projections,
		collections.requests,
		collections.scouting,
		collections.sitelog,
		collections.sms,
		collections.awards,
		collections.dblog,
	];

	const collectionResults = await Promise.all(collectionNames.map((name) => deleteAllDocsInCollection(name, targetDB)));
	const collectionsSuccess = collectionResults.every(Boolean);
	const applicantsSuccess = await clearApplicantsApplications(targetDB);

	return collectionsSuccess && applicantsSuccess;
};

export const logEvent = async (actionDescription = '', errorDetails: unknown = null) => {
	try {
		const ipResponse = await axios.get(`https://ipapi.co/json/`);
		const locationData = ipResponse.data;

		const parser = new UAParser();
		const browserInfo = parser.getResult();

		const logData = {
			timestamp: new Date(),
			ip: locationData.ip || 'unknown',
			city: locationData.city,
			country: locationData.country_name,
			region: locationData.region,
			lat: locationData.latitude,
			lon: locationData.longitude,
			isp: locationData.org,
			org: locationData.org,
			timezone: locationData.timezone,
			userAgent: browserInfo.ua || 'unknown',
			browser: browserInfo.browser?.name || 'unknown',
			browserVersion: browserInfo.browser?.version || 'unknown',
			os: browserInfo.os?.name || 'unknown',
			osVersion: browserInfo.os?.version || 'unknown',
			device: {
				vendor: browserInfo.device?.vendor || 'unknown',
				model: browserInfo.device?.model || 'unknown',
				type: browserInfo.device?.type || 'unknown',
			},
			actionDescription: actionDescription || 'none',
			authDetails: auth.currentUser,
			errorDetails: errorDetails,
		};

		const cleanedLogData = JSON.parse(JSON.stringify(logData, (key, value) => (value === undefined ? null : value)));
		await addDoc(collection(db, collections.sitelog), cleanedLogData);

		if (errorDetails !== null && process.env.REACT_APP_environment !== 'production') {
			const errMessage = errorDetails instanceof Error ? errorDetails.message : String(errorDetails);
			console.error(`Error!\nDescription: ${actionDescription}\nMessage: ${errMessage}\nError:`, errorDetails);
		}
	} catch (error) {
		console.error('Error logging user action:', error instanceof Error ? error.message : error);
	}
};

export const getConfigFromDb = async () => {
	try {
		const docRef = doc(db, collections.siteConfig, CONFIG_DOC_ID);
		const docSnap = await getDoc(docRef);
		return docSnap.exists() ? docSnap.data() : null;
	} catch (error) {
		logEvent('Error in getConfigFromDB', error);
		return null;
	}
};

export const getEmailLogs = async (limitCount = 20) => {
	try {
		const q = query(collection(db, collections.emails), orderBy('createdAt', 'desc'), limit(limitCount));
		const querySnapshot = await getDocs(q);
		return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
	} catch (error) {
		console.warn('Error fetching ordered email logs, falling back to unordered:', error);
		try {
			// Fallback: just get the documents without sorting in case index isn't ready
			const q = query(collection(db, collections.emails), limit(limitCount));
			const querySnapshot = await getDocs(q);
			return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
		} catch (innerError) {
			console.error('Final error fetching email logs:', innerError);
			return [];
		}
	}
};

export const updateUserPreferences = async (userId: string, collectionName: CollectionName, preferences: Record<string, unknown>) => {
	if (!userId || !collectionName) return;

	try {
		const userRef = doc(db, collectionName, userId);
		const updateData: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(preferences)) {
			updateData[`preferences.${key}`] = value;
		}

		await updateDoc(userRef, updateData as UpdateData<DocumentData>);
	} catch (error) {
		console.error('Error updating user preferences:', error);
	}
};

/**
 * Fetches multiple documents from a single collection by their IDs.
 * @param {string} collectionName - The name of the collection to query.
 * @param {string[]} ids - An array of document IDs to fetch.
 * @returns {Promise<object[]>} A promise that resolves to an array of document data.
 */
export const getDocumentsByIDs = async (collectionName: CollectionName, ids: string[]) => {
	if (!Array.isArray(ids) || ids.length === 0) {
		return [];
	}

	try {
		const docPromises = ids.map((id) => getDoc(doc(db, collectionName, id)));
		const docSnaps = await Promise.all(docPromises);
		return docSnaps.filter((doc) => doc.exists()).map((doc) => doc.data());
	} catch (error) {
		console.error(`Error in getDocumentsByIDs for collection ${collectionName}:`, error);
		return [];
	}
};

const searchSingleCollection = async (collectionInfo: { collection: string; fields: readonly string[] }, termsArray: string[], uniqueResults: Map<string, Record<string, unknown>>) => {
	const { collection: collectionName, fields } = collectionInfo;
	for (const term of termsArray) {
		const lowerTerm = term.toLowerCase();
		for (const field of fields) {
			const q = query(collection(db, collectionName), where(field, '>=', lowerTerm), where(field, '<=', lowerTerm + '\uf8ff'));
			const querySnapshot = await getDocs(q);
			for (const doc of querySnapshot.docs) {
				if (!uniqueResults.has(doc.id)) {
					const docData = doc.data();
					const document = { id: doc.id, ...docData, collection: collectionName };
					uniqueResults.set(doc.id, document);
				}
			}
		}
	}
};

export const searchCollections = async (termsArray: string[]) => {
	if (!Array.isArray(termsArray) || termsArray.length === 0) {
		throw new TypeError('Input must be a non-empty array of terms');
	}

	try {
		const uniqueResults = new Map();
		const searchPromises = Object.values(SearchableCollections).map((collectionInfo) => searchSingleCollection(collectionInfo, termsArray, uniqueResults));
		await Promise.all(searchPromises);
		return Array.from(uniqueResults.values());
	} catch (error) {
		console.error('Error searching collections:', error);
		throw error;
	}
};

export const getRealTimeConfigFromDb = (callback: RealtimeCallback) => {
	const docRef = doc(db, collections.siteConfig, CONFIG_DOC_ID);
	const unsubscribe = onSnapshot(docRef, (snapshot) => {
		snapshot.exists() ? callback(snapshot.data()) : callback(null);
	});
	return unsubscribe;
};

export const getApplicant = async (userID: string) => {
	try {
		const docRef = doc(db, collections.applicants, userID);
		const docSnap = await getDoc(docRef);
		return docSnap.exists() ? docSnap.data() : null;
	} catch (error) {
		logEvent('Error in getApplicant', error);
		return null;
	}
};

export const getMember = async (userID: string) => {
	try {
		const docRef = doc(db, collections.members, userID);
		const docSnap = await getDoc(docRef);
		return docSnap.exists() ? docSnap.data() : null;
	} catch (error) {
		logEvent('Error in getMember', error);
		return null;
	}
};

export const saveApplicantData = async (userID: string, data: Record<string, unknown>) => {
	try {
		const normalizedData = normalizeApplicantGradYearField({
			...data,
			...(typeof data.email === 'string' ? { email: data.email.trim().toLowerCase() } : {}),
		});
		const newRef = doc(db, collections.applicants, userID);
		await setDoc(newRef, normalizedData, { merge: true });
		return true;
	} catch (error) {
		logEvent('Error in saveApplicantData', error);
		return false;
	}
};

export const updateApplicantData = async (userID: string, data: Record<string, unknown>) => {
	try {
		const normalizedData = normalizeApplicantGradYearField({
			...data,
			...(typeof data.email === 'string' ? { email: data.email.trim().toLowerCase() } : {}),
		});
		const newRef = doc(db, collections.applicants, userID);
		await updateDoc(newRef, normalizedData as UpdateData<DocumentData>);
		return true;
	} catch (error) {
		logEvent('Error in updateApplicantData', error);
		return false;
	}
};

export const findApplicantAccountsByEmail = async (email: string, excludeId?: string) => {
	const normalized = email.trim().toLowerCase();
	if (!normalized) return [];

	const queries = normalized === email.trim()
		? [query(collection(db, collections.applicants), where('email', '==', normalized))]
		: [
				query(collection(db, collections.applicants), where('email', '==', normalized)),
				query(collection(db, collections.applicants), where('email', '==', email.trim())),
			];

	const matches = new Map<string, Record<string, unknown>>();
	for (const q of queries) {
		const snapshot = await getDocs(q);
		for (const docSnap of snapshot.docs) {
			if (excludeId && docSnap.id === excludeId) continue;
			matches.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
		}
	}

	return [...matches.values()];
};

export const addApplicationToApplicant = async (userID: string, applicationID: string) => {
	try {
		const applicantRef = doc(db, collections.applicants, userID);
		await updateDoc(applicantRef, { applications: arrayUnion(applicationID) });
		return true;
	} catch (error) {
		logEvent('Error in addApplicationToApplicant', error);
		return false;
	}
};

/** Drop undefined fields — Firestore rejects undefined in setDoc/updateDoc. */
export const omitUndefinedFields = <T extends Record<string, unknown>>(data: T): Partial<T> => {
	const out: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(data)) {
		if (value !== undefined) out[key] = value;
	}
	return out as Partial<T>;
};

export const saveCollectionData = async (collectionName: CollectionName, dataID: string, data: Record<string, unknown>) => {
	try {
		const newRef = doc(db, collectionName, dataID);
		await setDoc(newRef, omitUndefinedFields(data), { merge: true });
		return true;
	} catch (error) {
		logEvent('saveCollectionData error', error);
		return false;
	}
};

/** Like saveCollectionData but throws on failure (do not ignore write errors). */
export const saveCollectionDataOrThrow = async (collectionName: CollectionName, dataID: string, data: Record<string, unknown>) => {
	const newRef = doc(db, collectionName, dataID);
	await setDoc(newRef, omitUndefinedFields(data), { merge: true });
	return true;
};

export const updateCollectionData = async (collectionName: CollectionName, dataID: string, data: Record<string, unknown>) => {
	try {
		const newRef = doc(db, collectionName, dataID);
		await updateDoc(newRef, data as UpdateData<DocumentData>);
		return true;
	} catch (error) {
		logEvent('updateCollectionData error', error);
		return false;
	}
};

export const updateApplicationStatus = async (userID: string, applicationID: string, newStatus: string) => {
	try {
		await updateCollectionData(collections.applications, applicationID, { status: newStatus });
		return true;
	} catch (error) {
		logEvent('Error updating application status:', error);
		return false;
	}
};

export const getApplication = async (userID: string, applicationID: string) => {
	try {
		if (applicationID) {
			const docRef = doc(db, collections.applications, applicationID);
			const docSnap = await getDoc(docRef);
			return docSnap.exists() ? docSnap.data() : null;
		}
	} catch (error) {
		logEvent('getApplication error', error);
		return null;
	}
};

export const getCollection = async (collectionName: CollectionName) => {
	try {
		const collector = [];
		const querySnapshot = await getDocs(collection(db, collectionName));
		for (const doc of querySnapshot.docs) {
			collector.push(doc.data());
		}
		return collector;
	} catch (error) {
		logEvent('getCollection error', error);
		return null;
	}
};

const getOrderBy = (orderByConfig: unknown) => {
	if (!orderByConfig || !Array.isArray(orderByConfig) || orderByConfig.length === 0) {
		return [];
	}
	const configArray = orderByConfig as Array<[string, 'asc' | 'desc'] | string>;
	if (Array.isArray(configArray[0])) {
		return (configArray as Array<[string, 'asc' | 'desc']>).map((config) => orderBy(config[0], config[1]));
	}
	const single = configArray as unknown as [string, 'asc' | 'desc'];
	return [orderBy(single[0], single[1])];
};

/**
 * Fetches the legacy finances collection and maps the doc ID (year) to 'id'.
 */
export const getRealTimeLegacyFinances = (
	handler: RealtimeCallback,
	{ collection: collectionName, orderBy: orderByConfig }: { collection: CollectionName; orderBy: unknown }
) => {
	if (typeof collectionName !== 'string' || collectionName.length === 0) {
		console.error('getRealTimeLegacyFinances error: collectionName is not a valid string.', collectionName);
		handler([]);
		return () => {};
	}

	try {
		const collRef = collection(db, collectionName);
		const q = query(collRef, ...getOrderBy(orderByConfig));

		return onSnapshot(
			q,
			(snapshot) => {
				const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
				handler(data);
			},
			(error) => {
				console.error(`Error listening to collection ${collectionName}:`, error);
				handler([]);
			}
		);
	} catch (error) {
		console.error(`Failed to create query for ${collectionName}:`, error);
		handler([]);
		return () => {};
	}
};

export const getRealTimeYearlyFinances = (handler: RealtimeCallback) => {
	return getRealTimeLegacyFinances(handler, {
		collection: collections.yearlyFinances,
		orderBy: [['cycleYear', 'desc']],
	});
};

export const ensureYearlyFinancesDoc = async (cycleYear: number) => {
	const yearId = String(cycleYear);
	const ref = doc(db, collections.yearlyFinances, yearId);
	const snap = await getDoc(ref);
	if (!snap.exists()) {
		const { emptyYearlyFinances } = await import('./yearlyFinances');
		await setDoc(ref, emptyYearlyFinances(cycleYear), { merge: true });
	}
	return yearId;
};

export const saveYearlyFinancesDoc = async (cycleYear: number, data: Record<string, unknown>) => {
	return saveCollectionData(collections.yearlyFinances, String(cycleYear), {
		...data,
		cycleYear,
	});
};

/** Index an award id onto yearlyFinances/{cycleYear}.awardIDs (awards collection remains SoT). */
export const linkAwardToYearlyFinances = async (cycleYear: number, awardId: string) => {
	if (!cycleYear || !awardId) return false;
	try {
		await ensureYearlyFinancesDoc(cycleYear);
		const yearRef = doc(db, collections.yearlyFinances, String(cycleYear));
		await updateDoc(yearRef, { awardIDs: arrayUnion(awardId), cycleYear });
		return true;
	} catch (error) {
		logEvent('linkAwardToYearlyFinances error', error);
		return false;
	}
};

export type ApplicationAwardInput = {
	applicationId: string;
	applicantId: string;
	memberId: string;
	type: string;
	amount: number | string;
	cycleYear: number;
	deadline?: unknown;
	followUp?: string;
	/** When false, skip applicant push notice (bulk imports). Default true. */
	notify?: boolean;
};

/**
 * Create awards/{id}, link onto applications/{id}.awards, set status Awarded, index yearlyFinances.
 * Requires memberId (auth uid of the member recording the award).
 */
export const createApplicationAward = async (input: ApplicationAwardInput) => {
	const memberId = String(input.memberId || '').trim();
	if (!memberId) throw new Error('memberId is required to create an award');
	const applicationId = String(input.applicationId || '').trim();
	if (!applicationId) throw new Error('applicationId is required');
	const applicantId = String(input.applicantId || '').trim();
	if (!applicantId) throw new Error('applicantId is required');
	const type = String(input.type || '').trim();
	if (!type) throw new Error('award type is required');
	const amount = Number(input.amount);
	if (!Number.isFinite(amount) || amount < 0) throw new Error('award amount must be a non-negative number');
	const cycleYear = Number(input.cycleYear);
	if (!Number.isFinite(cycleYear) || cycleYear < 2000) throw new Error('cycleYear is required');

	const awardId = uuid();
	const awardData = omitUndefinedFields({
		id: awardId,
		awardID: awardId,
		memberId,
		applicationId,
		applicationID: applicationId,
		applicantId,
		applicantID: applicantId,
		date: serverTimestamp(),
		createdOn: new Date().toISOString(),
		type,
		amount,
		awardAmount: amount,
		followUp: input.followUp ? String(input.followUp).trim() || undefined : undefined,
		cycleYear,
		deadline: input.deadline ?? null,
	});

	await saveCollectionDataOrThrow(collections.awards, awardId, awardData);

	const appRef = doc(db, collections.applications, applicationId);
	await updateDoc(appRef, {
		status: ApplicationStatus.awarded,
		awards: arrayUnion(awardId),
		cycleYear,
	});

	const linked = await linkAwardToYearlyFinances(cycleYear, awardId);
	if (!linked) {
		throw new Error(`Award ${awardId} saved but failed to index onto yearlyFinances/${cycleYear}`);
	}

	return { awardId, awardData };
};

export const createApplicationAwardsBulk = async (rows: ApplicationAwardInput[]) => {
	const results: Array<{ ok: boolean; awardId?: string; applicationId: string; type: string; amount: number | string; error?: string }> = [];
	for (const row of rows) {
		try {
			const { awardId } = await createApplicationAward({ ...row, notify: row.notify ?? false });
			results.push({ ok: true, awardId, applicationId: row.applicationId, type: row.type, amount: row.amount });
		} catch (error) {
			results.push({
				ok: false,
				applicationId: row.applicationId,
				type: row.type,
				amount: row.amount,
				error: error instanceof Error ? error.message : String(error),
			});
		}
	}
	return results;
};

/** Set memberId on every awards/{id} missing one. */
export const backfillAwardMemberIds = async (memberId: string) => {
	const uid = String(memberId || '').trim();
	if (!uid) throw new Error('memberId is required');
	const snapshot = await getDocs(collection(db, collections.awards));
	let updated = 0;
	for (const docSnap of snapshot.docs) {
		const data = docSnap.data() || {};
		if (data.memberId || data.memberID) continue;
		await updateDoc(docSnap.ref, { memberId: uid });
		updated++;
	}
	return { scanned: snapshot.size, updated };
};

/** Awarded applications for a cycle year that have no awards/{id} linked by applicationID. */
export const getAwardedApplicationsMissingAwards = async (cycleYear: number) => {
	const apps = ((await getApplicationsByWindow(cycleYear)) || []) as Array<Record<string, unknown> & { id: string }>;
	const awardsSnap = await getDocs(collection(db, collections.awards));
	const awardedAppIds = new Set<string>();
	for (const docSnap of awardsSnap.docs) {
		const data = docSnap.data() || {};
		const appId = String(data.applicationID || data.applicationId || '');
		if (appId) awardedAppIds.add(appId);
	}
	const missing = apps
		.filter((app) => String(app.status) === ApplicationStatus.awarded)
		.filter((app) => !awardedAppIds.has(String(app.id)));

	return Promise.all(
		missing.map(async (app) => {
			let profile: Record<string, unknown> | null =
				app.profile && typeof app.profile === 'object' ? (app.profile as Record<string, unknown>) : null;
			if (!profile && app.completedBy) {
				const profileId = typeof app.profile === 'string' && app.profile ? app.profile : String(app.completedBy);
				const snap = await getDoc(doc(db, collections.profiles, profileId));
				if (snap.exists()) profile = snap.data() as Record<string, unknown>;
				else {
					const applicantSnap = await getDoc(doc(db, collections.applicants, String(app.completedBy)));
					if (applicantSnap.exists()) profile = applicantSnap.data() as Record<string, unknown>;
				}
			}
			return {
				id: String(app.id),
				type: String(app.type || ''),
				completedBy: String(app.completedBy || ''),
				window: app.window,
				cycleYear: resolveApplicationCycleYear(app),
				awards: Array.isArray(app.awards) ? app.awards : [],
				profile,
			};
		})
	);
};

export const unlinkAwardFromYearlyFinances = async (cycleYear: number, awardId: string) => {
	if (!cycleYear || !awardId) return false;
	try {
		const yearRef = doc(db, collections.yearlyFinances, String(cycleYear));
		await updateDoc(yearRef, { awardIDs: arrayRemove(awardId) });
		return true;
	} catch (error) {
		logEvent('unlinkAwardFromYearlyFinances error', error);
		return false;
	}
};

export const getAwardsByIds = async (awardIds: string[]) => {
	const unique = [...new Set((awardIds || []).filter(Boolean))];
	const docs = await Promise.all(
		unique.map(async (id) => {
			const snap = await getDoc(doc(db, collections.awards, id));
			return snap.exists() ? { id: snap.id, ...snap.data() } : null;
		})
	);
	return docs.filter(Boolean) as Record<string, unknown>[];
};

export const getApplicationsByIds = async (applicationIds: string[]) => {
	const unique = [...new Set((applicationIds || []).filter(Boolean))];
	const docs = await Promise.all(
		unique.map(async (id) => {
			const snap = await getDoc(doc(db, collections.applications, id));
			return snap.exists() ? { id: snap.id, ...snap.data() } : null;
		})
	);
	return docs.filter(Boolean) as Record<string, unknown>[];
};

/** Format a Places-style or plain mailing address. Never treats bare emails as mail addresses. */
export const formatApplicantMailingAddress = (value: unknown): string | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) return null;
		if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return null;
		return trimmed;
	}
	if (typeof value === 'object') {
		const obj = value as Record<string, unknown>;
		const description = typeof obj.description === 'string' ? obj.description.trim() : '';
		if (description && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(description)) return description;
		const structured = obj.structured_formatting as Record<string, unknown> | undefined;
		if (structured) {
			const parts = [structured.main_text, structured.secondary_text].filter(Boolean).map(String);
			if (parts.length) return parts.join(', ');
		}
		const parts = [obj.address, obj.street, obj.address2, obj.city, obj.state, obj.zip, obj.postalCode, obj.zipCode]
			.filter(Boolean)
			.map(String);
		if (parts.length) return parts.join(', ');
	}
	return null;
};

export const getMemberOrApplicantAddress = async (
	userId: string,
	options?: { profileId?: string | null }
) => {
	const missingMail = 'No mailing address on file';
	if (!userId && !options?.profileId) return { name: 'Unknown', address: missingMail };

	const profileIds = [...new Set([options?.profileId, userId].filter(Boolean).map(String))];
	for (const profileId of profileIds) {
		const snap = await getDoc(doc(db, collections.profiles, profileId));
		if (!snap.exists()) continue;
		const data = snap.data() as Record<string, unknown>;
		const name =
			[data.applicantFirstName, data.applicantLastName].filter(Boolean).join(' ') ||
			String(data.callMe || data.firstName || '') ||
			'Unknown';
		const address = formatApplicantMailingAddress(data.applicantMailingAddress);
		return { name: name === 'Unknown' && !address ? 'Unknown' : name || 'Unknown', address: address || missingMail };
	}

	for (const coll of [collections.applicants, collections.members]) {
		if (!userId) continue;
		const snap = await getDoc(doc(db, coll, userId));
		if (!snap.exists()) continue;
		const data = snap.data() as Record<string, unknown>;
		const name =
			[data.firstName, data.lastName].filter(Boolean).join(' ') || String(data.callMe || 'Unknown');
		const address =
			formatApplicantMailingAddress(data.applicantMailingAddress) ||
			formatApplicantMailingAddress(data.mailingAddress) ||
			(() => {
				const parts = [data.address, data.address2, data.city, data.state, data.zip, data.postalCode]
					.filter(Boolean)
					.map(String);
				return parts.length ? parts.join(', ') : null;
			})();
		return { name, address: address || missingMail };
	}

	return { name: 'Unknown', address: missingMail };
};

export const migrateLegacyFinancesToYearly = async () => {
	const { mapLegacyFinanceToYearly } = await import('./yearlyFinances');
	const snapshot = await getDocs(collection(db, collections.legacyFinances));
	let count = 0;
	for (const legacyDoc of snapshot.docs) {
		const legacy = { id: legacyDoc.id, ...legacyDoc.data() } as Record<string, unknown>;
		if (legacy.year == null && legacyDoc.id) legacy.year = Number(legacyDoc.id);
		const mapped = mapLegacyFinanceToYearly(legacy as Parameters<typeof mapLegacyFinanceToYearly>[0], serverTimestamp());
		const yearId = String(mapped.cycleYear);
		const existing = await getDoc(doc(db, collections.yearlyFinances, yearId));
		const existingAwardIds = existing.exists() ? ((existing.data()?.awardIDs as string[]) || []) : [];
		await setDoc(
			doc(db, collections.yearlyFinances, yearId),
			{
				...mapped,
				awardIDs: existingAwardIds,
			},
			{ merge: true }
		);
		count += 1;
	}
	return count;
};

/**
 * Index every awards/{id} onto yearlyFinances/{cycleYear}.awardIDs by award.cycleYear.
 * Safe to re-run (arrayUnion). Creates missing year hub docs.
 */
export const backfillAwardIdsIntoYearlyFinances = async () => {
	const snapshot = await getDocs(collection(db, collections.awards));
	const byYear = new Map<number, string[]>();
	for (const awardDoc of snapshot.docs) {
		const cycleYear = Number(awardDoc.data()?.cycleYear);
		if (!Number.isFinite(cycleYear) || cycleYear <= 0) continue;
		const list = byYear.get(cycleYear) || [];
		list.push(awardDoc.id);
		byYear.set(cycleYear, list);
	}

	let yearsTouched = 0;
	let awardsLinked = 0;
	for (const [cycleYear, ids] of byYear.entries()) {
		await ensureYearlyFinancesDoc(cycleYear);
		const yearRef = doc(db, collections.yearlyFinances, String(cycleYear));
		// Firestore arrayUnion supports multiple values
		await updateDoc(yearRef, { awardIDs: arrayUnion(...ids), cycleYear });
		yearsTouched += 1;
		awardsLinked += ids.length;
	}
	return { yearsTouched, awardsLinked, awardDocs: snapshot.size };
};

export const getRealTimeCollection = (collectionRef: CollectionName, callback: RealtimeCallback, onError?: (error: Error) => void) => {
	const unsubscribe = onSnapshot(
		collection(db, collectionRef),
		(snapshot) => {
			const fetchedData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
			callback(fetchedData);
		},
		(error) => {
			onError?.(error);
		}
	);

	return unsubscribe;
};

const requestStatusRank = (request: DocumentData): number => {
	if (request.completed) return 1;
	const expiry = request.expiryDate ? firestoreValueToDate(request.expiryDate).getTime() : Number.NaN;
	const expired = !Number.isNaN(expiry) && expiry < Date.now();
	const attempts = typeof request.attempts === 'number' ? request.attempts : Number(request.attempts) || 0;
	if (expired || attempts > 0) return 2;
	return 0;
};

const sortReferenceRequests = (requests: DocumentData[]): DocumentData[] =>
	[...requests].sort((a, b) => {
		const statusDiff = requestStatusRank(a) - requestStatusRank(b);
		if (statusDiff !== 0) return statusDiff;
		return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });
	});

export const getRealTimeRequestsForCycleYear = (cycleYear: number, callback: RealtimeCallback) => {
	let requests: DocumentData[] = [];
	let appsById = new Map<string, DocumentData>();

	const publish = () => {
		const filtered = requests.filter((request) => {
			const applicationId = String(request.applicationID ?? '');
			if (!applicationId) return false;
			const application = appsById.get(applicationId);
			if (!application) return false;
			return resolveApplicationCycleYear(application) === cycleYear;
		});
		callback(sortReferenceRequests(filtered));
	};

	const unsubRequests = onSnapshot(
		collection(db, collections.requests),
		(snapshot) => {
			requests = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
			publish();
		},
		(error) => {
			logEvent('getRealTimeRequestsForCycleYear requests error', error);
			callback([]);
		}
	);

	const unsubApps = onSnapshot(
		collection(db, collections.applications),
		(snapshot) => {
			appsById = new Map(snapshot.docs.map((docSnap) => [docSnap.id, docSnap.data()]));
			publish();
		},
		(error) => {
			logEvent('getRealTimeRequestsForCycleYear applications error', error);
		}
	);

	return () => {
		unsubRequests();
		unsubApps();
	};
};

export const getPastApplicationsCountByWindow = async (cycleYear: number) => {
	try {
		const coll = collection(db, collections.applications);
		const q = query(
			coll,
			and(
				where('cycleYear', '==', cycleYear),
				or(where('status', '==', ApplicationStatus.eligible), where('status', '==', ApplicationStatus.invited), where('status', '==', ApplicationStatus.deferred), where('status', '==', ApplicationStatus.awarded), where('status', '==', ApplicationStatus.denied))
			)
		);

		const snapshot = await getCountFromServer(q);
		return snapshot.data().count;
	} catch (error) {
		logEvent('Error in getPastApplicationsCountByWindow', error);
		return null;
	}
};

export const getCurrentApplicationCount = async () => {
	try {
		const config = await getConfigFromDb();
		const coll = collection(db, collections.applications);
		const q = query(coll, where('cycleYear', '==', siteConfigCycleYear(config)));
		const snapshot = await getCountFromServer(q);
		return snapshot.data().count;
	} catch (error) {
		logEvent('Error in getCurrentApplicationCount', error);
		return null;
	}
};

export const getRealTimeCurrentApplicationCount = async (callback: RealtimeCallback<number>) => {
	try {
		const config = await getConfigFromDb();
		const year = siteConfigCycleYear(config);
		const coll = collection(db, collections.applications);
		const unsubscribe = onSnapshot(coll, (snapshot) => {
			const count = snapshot.docs.reduce((acc, d) => {
				const data = d.data();
				if (resolveApplicationCycleYear(data) !== year) return acc;
				if (data.status === ApplicationStatus.deleted) return acc;
				return acc + 1;
			}, 0);
			callback(count);
		});

		return unsubscribe;
	} catch (error) {
		logEvent('getRealTimeCurrentApplicationCount error', error);
		return null;
	}
};

export const getRealTimeCurrentApplications = async (callback: RealtimeCallback) => {
	try {
		const config = await getConfigFromDb();
		const year = siteConfigCycleYear(config);
		const coll = collection(db, collections.applications);
		const unsubscribe = onSnapshot(coll, (snapshot) => {
			const apps = snapshot.docs
				.map((d) => ({ id: d.id, ...d.data() }))
				.filter((app) => resolveApplicationCycleYear(app) === year && (app as DocumentData).status !== ApplicationStatus.deleted);
			callback(apps);
		});
		return unsubscribe;
	} catch (error) {
		logEvent('getRealTimeCurrentApplications error', error);
		return null;
	}
};

export const getCurrentlyEligibleApplicationsCount = async () => {
	try {
		const config = await getConfigFromDb();
		const coll = collection(db, collections.applications);
		const q = query(
			coll,
			and(
				where('cycleYear', '==', siteConfigCycleYear(config)),
				or(
					where('status', '==', ApplicationStatus.completed),
					where('status', '==', ApplicationStatus.eligible),
					where('status', '==', ApplicationStatus.invited),
					where('status', '==', ApplicationStatus.deferred),
					where('status', '==', ApplicationStatus.awarded),
					where('status', '==', ApplicationStatus.denied)
				)
			)
		);

		const snapshot = await getCountFromServer(q);
		return snapshot.data().count;
	} catch (error) {
		logEvent('Error in getCurrentlyEligibleApplicationsCount', error);
		return null;
	}
};

export const getMostRecentApplicationIDs = async (limitCount = 10) => {
	try {
		const config = await getConfigFromDb();
		const cycleYear = siteConfigCycleYear(config);
		const q = query(collection(db, collections.applications), orderBy('lastUpdated', 'desc'), limit(limitCount * 5));
		const querySnapshot = await getDocs(q);
		return recentApplicationIdsFromDocs(querySnapshot.docs, limitCount, cycleYear);
	} catch (error) {
		logEvent('Error fetching most recent application IDs: ', error);
		return [];
	}
};

export const getRealTimeMostRecentApplicationIDs = (callback: RealtimeCallback<string[]>, limitCount = 10) => {
	let cycleYear = new Date().getFullYear();
	let configLoaded = false;
	let latestDocs: { id: string; data: () => DocumentData }[] = [];

	const publish = () => {
		if (!configLoaded) return;
		callback(recentApplicationIdsFromDocs(latestDocs, limitCount, cycleYear));
	};

	void getConfigFromDb()
		.then((config) => {
			cycleYear = siteConfigCycleYear(config);
			configLoaded = true;
			publish();
		})
		.catch((error) => {
			logEvent('Error loading config for getRealTimeMostRecentApplicationIDs', error);
			configLoaded = true;
			publish();
		});

	const unsubscribe = onSnapshot(collection(db, collections.applications), (snapshot) => {
		latestDocs = snapshot.docs;
		publish();
	});

	return unsubscribe;
};

export const getApplicationsByYear = async () => {
	const config = await getConfigFromDb();
	const currentYear = siteConfigCycleYear(config);
	const excludedStatuses = new Set<string>([
		ApplicationStatus.deleted,
		ApplicationStatus.ineligible,
		ApplicationStatus.deferred,
	]);

	const groupedData: Record<number, number> = {};
	for (let year = currentYear - 3; year <= currentYear; year++) {
		groupedData[year] = 0;
	}

	try {
		const coll = collection(db, collections.applications);
		const snapshot = await getDocs(query(coll));

		for (const docSnap of snapshot.docs) {
			const data = docSnap.data();
			if (excludedStatuses.has(String(data.status))) continue;

			const year = resolveApplicationCycleYear(data);
			if (year === null || year < currentYear - 3 || year > currentYear) continue;

			groupedData[year]++;
		}
	} catch (error) {
		logEvent('Error in getApplicationsByYear', error);
	}

	return Object.keys(groupedData)
		.sort((a, b) => Number(a) - Number(b))
		.map((year) => ({
			name: year,
			count: groupedData[Number(year)],
		}));
};

export const getCurrentlyEligibleApplicationsCountByType = async (type: string) => {
	try {
		const config = await getConfigFromDb();
		const coll = collection(db, collections.applications);
		const q = query(
			coll,
			and(
				where('type', '==', type),
				where('cycleYear', '==', siteConfigCycleYear(config)),
				or(
					where('status', '==', ApplicationStatus.completed),
					where('status', '==', ApplicationStatus.eligible),
					where('status', '==', ApplicationStatus.invited),
					where('status', '==', ApplicationStatus.deferred),
					where('status', '==', ApplicationStatus.awarded),
					where('status', '==', ApplicationStatus.denied)
				)
			)
		);
		const snapshot = await getCountFromServer(q);
		return snapshot.data().count;
	} catch (error) {
		logEvent('getCurrentlyEligibleApplicationsCountByType error', error);
		return null;
	}
};

export const getRealTimeCurrentEligibleApplicationsCountByType = async (type: string, callback: RealtimeCallback<number>) => {
	try {
		const config = await getConfigFromDb();
		const coll = collection(db, collections.applications);
		const year = siteConfigCycleYear(config);

		let q;

		if (isApplicationTypeValue(type)) {
			q = query(
				coll,
				and(
					where('type', '==', type),
					where('cycleYear', '==', year),
					or(
						where('status', '==', ApplicationStatus.completed),
						where('status', '==', ApplicationStatus.eligible),
						where('status', '==', ApplicationStatus.invited),
						where('status', '==', ApplicationStatus.deferred),
						where('status', '==', ApplicationStatus.awarded),
						where('status', '==', ApplicationStatus.denied)
					)
				)
			);
		} else if (isApplicationStatusValue(type)) {
			q = query(coll, and(where('cycleYear', '==', year), where('status', '==', type)));
		} else {
			throw new Error(`Invalid type passed to getRealTimeCurrentEligibleApplicationsCountByType: ${type}`);
		}

		const unsubscribe = onSnapshot(q, (snapshot) => {
			callback(snapshot.size);
		});

		return unsubscribe;
	} catch (error) {
		logEvent('Error in getRealTimeCurrentEligibleApplicationsCountByType', error);
		return null;
	}
};

export const getEligibleApplicationsCountByTypeAndWindow = async (type: string, cycleYear: number) => {
	try {
		const coll = collection(db, collections.applications);
		const q = query(
			coll,
			and(
				where('type', '==', type),
				where('cycleYear', '==', cycleYear),
				or(
					where('status', '==', ApplicationStatus.completed),
					where('status', '==', ApplicationStatus.eligible),
					where('status', '==', ApplicationStatus.invited),
					where('status', '==', ApplicationStatus.deferred),
					where('status', '==', ApplicationStatus.awarded),
					where('status', '==', ApplicationStatus.denied)
				)
			)
		);
		const snapshot = await getCountFromServer(q);
		return snapshot.data().count;
	} catch (error) {
		logEvent('Error in getEligibleApplicationsCountByTypeAndWindow', error);
		return null;
	}
};

export const getRealTimeCurrentNonNegatedApplicationsCountByType = async (type: string, callback: RealtimeCallback<number>) => {
	try {
		const config = await getConfigFromDb();
		const coll = collection(db, collections.applications);
		const year = siteConfigCycleYear(config);
		const q = query(coll, where('type', '==', type));
		return onSnapshot(q, (snapshot) => {
			const count = snapshot.docs.reduce((acc, d) => {
				const data = d.data();
				if (resolveApplicationCycleYear(data) !== year) return acc;
				const status = String(data.status ?? '');
				return NEGATABLE_STATUSES.has(status) ? acc : acc + 1;
			}, 0);
			callback(count);
		});
	} catch (error) {
		logEvent('Error in getRealTimeCurrentNonNegatedApplicationsCountByType', error);
		return null;
	}
};

export const getRealTimeNonNegatedApplicationsCountByTypeAndCycleYear = (type: string, cycleYear: number, callback: RealtimeCallback<number>) => {
	try {
		const coll = collection(db, collections.applications);
		const q = query(coll, where('type', '==', type));
		return onSnapshot(q, (snapshot) => {
			const count = snapshot.docs.reduce((acc, d) => {
				const data = d.data();
				if (resolveApplicationCycleYear(data) !== cycleYear) return acc;
				const status = String(data.status ?? '');
				return NEGATABLE_STATUSES.has(status) ? acc : acc + 1;
			}, 0);
			callback(count);
		});
	} catch (error) {
		logEvent('Error in getRealTimeNonNegatedApplicationsCountByTypeAndCycleYear', error);
		return null;
	}
};

export const getRealTimeCurrentNonNegatedApplicationsCountByStatus = async (status: string, callback: RealtimeCallback<number>) => {
	try {
		const config = await getConfigFromDb();
		const coll = collection(db, collections.applications);
		const year = siteConfigCycleYear(config);
		const q = query(coll, where('status', '==', status));
		return onSnapshot(q, (snapshot) => {
			const count = snapshot.docs.reduce((acc, d) => {
				const data = d.data();
				return resolveApplicationCycleYear(data) === year ? acc + 1 : acc;
			}, 0);
			callback(count);
		});
	} catch (error) {
		logEvent('Error in getRealTimeCurrentNonNegatedApplicationsCountByStatus', error);
		return null;
	}
};

const PRESENCE_ACTIVE_WINDOW_MS = 2 * 60 * 1000;
const PRESENCE_QUERY_REFRESH_MS = 30 * 1000;
const PRESENCE_DOC_TTL_MS = 10 * 60 * 1000;

export const touchUserPresence = async (params: { uid: string; role: UserTypeValue | null; displayName: string }) => {
	const expiresAt = new Date(Date.now() + PRESENCE_DOC_TTL_MS);
	await setDoc(
		doc(db, collections.presence, params.uid),
		{
			uid: params.uid,
			role: params.role,
			displayName: params.displayName,
			status: 'online',
			lastSeen: serverTimestamp(),
			expiresAt: Timestamp.fromDate(expiresAt),
		},
		{ merge: true }
	);
};

export const clearUserPresence = async (uid: string) => {
	await setDoc(
		doc(db, collections.presence, uid),
		{ status: 'offline', lastSeen: serverTimestamp() },
		{ merge: true }
	);
};

export const subscribeUserLastSeen = (uid: string, callback: (lastSeen: Date | null) => void): (() => void) => {
	if (!uid) {
		callback(null);
		return () => {};
	}

	const ref = doc(db, collections.presence, uid);
	return onSnapshot(
		ref,
		(snap) => {
			if (!snap.exists()) {
				callback(null);
				return;
			}
			const lastSeen = snap.data().lastSeen;
			callback(lastSeen ? firestoreValueToDate(lastSeen) : null);
		},
		() => callback(null)
	);
};

export const getRealTimeActiveAuthenticatedUsersCount = (callback: RealtimeCallback<number>) => {
	let unsubscribe: (() => void) | null = null;
	let refreshTimer: ReturnType<typeof setInterval> | null = null;

	const subscribe = () => {
		try {
			if (unsubscribe) {
				unsubscribe();
				unsubscribe = null;
			}

			const cutoff = Timestamp.fromDate(new Date(Date.now() - PRESENCE_ACTIVE_WINDOW_MS));
			const q = query(collection(db, collections.presence), where('lastSeen', '>', cutoff));

			unsubscribe = onSnapshot(
				q,
				(snapshot) => {
					const count = snapshot.docs.filter((docSnap) => docSnap.data().status === 'online').length;
					callback(count);
				},
				(error) => {
					logEvent('Error in getRealTimeActiveAuthenticatedUsersCount snapshot', error);
				}
			);
		} catch (error) {
			logEvent('Error in getRealTimeActiveAuthenticatedUsersCount', error);
		}
	};

	subscribe();
	refreshTimer = setInterval(subscribe, PRESENCE_QUERY_REFRESH_MS);

	return () => {
		if (unsubscribe) unsubscribe();
		if (refreshTimer) clearInterval(refreshTimer);
	};
};

export const getRealTimeApplicationCountByStatus = (status: string, callback: RealtimeCallback<number>) => {
	try {
		const coll = collection(db, collections.applications);
		const q = query(coll, where('status', '==', status));
		const unsubscribe = onSnapshot(q, (snapshot) => {
			callback(snapshot.size);
		});
		return unsubscribe;
	} catch (error) {
		logEvent('Error in getRealTimeApplicationCountByStatus', error);
		return null;
	}
};

export const getRealTimeCurrentApplicationCountByStatus = async (status: string, callback: RealtimeCallback<number>) => {
	try {
		const config = await getConfigFromDb();
		const year = siteConfigCycleYear(config);
		const coll = collection(db, collections.applications);
		const q = query(coll, where('status', '==', status));
		return onSnapshot(q, (snapshot) => {
			const count = snapshot.docs.reduce((acc, d) => {
				return resolveApplicationCycleYear(d.data()) === year ? acc + 1 : acc;
			}, 0);
			callback(count);
		});
	} catch (error) {
		logEvent('Error in getRealTimeCurrentApplicationCountByStatus', error);
		return null;
	}
};

export const getRealTimeCurrentRejectedApplicationsCount = async (callback: RealtimeCallback<number>) => {
	try {
		const config = await getConfigFromDb();
		const year = siteConfigCycleYear(config);
		const coll = collection(db, collections.applications);
		const q = query(
			coll,
			or(
				where('status', '==', ApplicationStatus.denied),
				where('status', '==', ApplicationStatus.deferred),
				where('status', '==', ApplicationStatus.ineligible)
			)
		);
		return onSnapshot(q, (snapshot) => {
			const count = snapshot.docs.reduce((acc, d) => {
				return resolveApplicationCycleYear(d.data()) === year ? acc + 1 : acc;
			}, 0);
			callback(count);
		});
	} catch (error) {
		logEvent('Error in getRealTimeCurrentRejectedApplicationsCount', error);
		return null;
	}
};

export const getRealTimeEligibleApplicationsCountByTypeAndWindow = (type: string, cycleYear: number, callback: RealtimeCallback<number>) => {
	try {
		const coll = collection(db, collections.applications);
		let q;

		if (isApplicationTypeValue(type)) {
			q = query(
				coll,
				and(
					where('type', '==', type),
					where('cycleYear', '==', cycleYear),
					or(
						where('status', '==', ApplicationStatus.completed),
						where('status', '==', ApplicationStatus.eligible),
						where('status', '==', ApplicationStatus.invited),
						where('status', '==', ApplicationStatus.deferred),
						where('status', '==', ApplicationStatus.awarded),
						where('status', '==', ApplicationStatus.denied)
					)
				)
			);
		} else if (isApplicationStatusValue(type)) {
			q = query(coll, and(where('cycleYear', '==', cycleYear), where('status', '==', type)));
		} else {
			throw new Error(`Invalid type passed to getRealTimeCurrentEligibleApplicationsCountByType: ${type}`);
		}

		const unsubscribe = onSnapshot(q, (snapshot) => {
			callback(snapshot.size);
		});

		return unsubscribe;
	} catch (error) {
		logEvent('Error in getRealTimeCurrentEligibleApplicationsCountByType', error);
		return null;
	}
};

export const getRealTimeApplications = (includeDeleted: boolean, callback: RealtimeCallback) => {
	try {
		const coll = collection(db, collections.applications);
		const q = includeDeleted ? coll : query(coll, where('status', '!=', ApplicationStatus.deleted));

		const unsubscribe = onSnapshot(q, (snapshot) => {
			const fetchedData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
			callback(sortApplicationsByLastTouched(fetchedData));
		});

		return unsubscribe;
	} catch (error) {
		logEvent('Error in getRealTimeApplications:', error);
		return null;
	}
};

export const getRealTimeRejectedApplications = (callback: RealtimeCallback, cycleYear: number | null = null) => {
	try {
		const coll = collection(db, collections.applications);

		const q = query(coll, or(where('status', '==', ApplicationStatus.denied), where('status', '==', ApplicationStatus.deferred), where('status', '==', ApplicationStatus.ineligible)));

		const unsubscribe = onSnapshot(q, (snapshot) => {
			let fetchedData = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
			if (cycleYear !== null) {
				fetchedData = fetchedData.filter((app) => resolveApplicationCycleYear(app) === cycleYear);
			}
			callback(sortApplicationsByLastTouched(fetchedData));
		});

		return unsubscribe;
	} catch (error) {
		logEvent('Error in getRealTimeRejectedApplications:', error);
		return null;
	}
};

const filterApplicationsForCycleYear = (
	apps: DocumentData[],
	cycleYear: number,
	includeDeleted: boolean
): DocumentData[] =>
	apps.filter((app) => {
		if (!includeDeleted && app.status === ApplicationStatus.deleted) return false;
		return resolveApplicationCycleYear(app) === cycleYear;
	});

export const getApplicationsByWindow = async (cycleYear: number) => {
	try {
		const querySnapshot = await getDocs(collection(db, collections.applications));
		return querySnapshot.docs
			.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
			.filter((app) => resolveApplicationCycleYear(app) === cycleYear)
			.sort((a, b) => applicationActivityTime(b) - applicationActivityTime(a));
	} catch (error) {
		logEvent('getApplicationsByWindow error', error);
		return null;
	}
};

export const getRealTimeApplicationsByWindow = (cycleYear: number, includeDeleted: boolean, callback: RealtimeCallback) => {
	const appsRef = collection(db, collections.applications);
	const unsubscribe = onSnapshot(
		appsRef,
		(snapshot) => {
			const fetchedData = filterApplicationsForCycleYear(
				snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
				cycleYear,
				includeDeleted
			);
			callback(sortApplicationsByLastTouched(fetchedData));
		},
		(error) => {
			logEvent('getRealTimeApplicationsByWindow error', error);
			callback([]);
		}
	);

	return unsubscribe;
};

export const getApplicationsByType = async (type: string, cycleYear: number) => {
	try {
		const q = query(collection(db, collections.applications), where('type', '==', type));
		const querySnapshot = await getDocs(q);
		return querySnapshot.docs
			.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
			.filter((app) => resolveApplicationCycleYear(app) === cycleYear)
			.sort((a, b) => applicationActivityTime(b) - applicationActivityTime(a));
	} catch (error) {
		logEvent('getApplicationsByType error', error);
		return null;
	}
};

export const getRealTimeApplicationsByType = (type: string, cycleYear: number, includeDeleted: boolean, callback: RealtimeCallback) => {
	const typeQuery = query(collection(db, collections.applications), where('type', '==', type));
	const unsubscribe = onSnapshot(
		typeQuery,
		(snapshot) => {
			const fetchedData = filterApplicationsForCycleYear(
				snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
				cycleYear,
				includeDeleted
			);
			callback(sortApplicationsByLastTouched(fetchedData));
		},
		(error) => {
			logEvent('getRealTimeApplicationsByType error', error);
			callback([]);
		}
	);

	return unsubscribe;
};

export const getRealTimeApplicantsByApplicationID = (applicantIDs: string[], callback: RealtimeCallback) => {
	if (Array.isArray(applicantIDs) && applicantIDs.length > 0) {
		const matches: Record<string, Record<string, unknown>> = {};
		const unsubscribeFunctions = applicantIDs.map((id) => {
			const docRef = doc(db, collections.applicants, id);
			return onSnapshot(
				docRef,
				(snapshot) => {
					if (snapshot.exists()) {
						matches[snapshot.id] = snapshot.data();
					} else {
						delete matches[snapshot.id];
					}
					callback(Object.values(matches));
				},
				(error) => {
					logEvent('Error in getRealTimeApplicantsByApplicationID', error);
				}
			);
		});

		return () => {
			for (const unsub of unsubscribeFunctions) {
				unsub();
			}
		};
	}
	return null;
};

export const getApplicationsByStatus = async (status: string, cycleYear: number) => {
	try {
		const q = query(collection(db, collections.applications), where('status', '==', status));
		const querySnapshot = await getDocs(q);
		return querySnapshot.docs
			.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
			.filter((app) => resolveApplicationCycleYear(app) === cycleYear)
			.sort((a, b) => applicationActivityTime(b) - applicationActivityTime(a));
	} catch (error) {
		logEvent('getApplicationsByStatus error', error);
		return null;
	}
};

export const getRealTimeApplicationsByStatus = (type: string, callback: RealtimeCallback, cycleYear: number | null = null) => {
	const statusQuery =
		cycleYear === null
			? query(collection(db, collections.applications), where('status', '==', type))
			: query(collection(db, collections.applications), where('status', '==', type));
	const unsubscribe = onSnapshot(statusQuery, (snapshot) => {
		let fetchedData = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
		if (cycleYear !== null) {
			fetchedData = fetchedData.filter((app) => resolveApplicationCycleYear(app) === cycleYear);
		}
		callback(sortApplicationsByLastTouched(fetchedData));
	});
	return unsubscribe;
};

export const getRealTimeDocument = (type: CollectionName, id: string, callback: RealtimeCallback) => {
	if (type && id && callback) {
		const docRef = doc(db, type, id);
		const unsubscribe = onSnapshot(docRef, (snapshot) => {
			snapshot.exists() ? callback({ id: snapshot.id, ...snapshot.data() }) : callback(null);
		});

		return unsubscribe;
	}
};

export const getRealTimeApplicationsByIDs = (applicationIDs: string[], callback: RealtimeCallback) => {
	if (!Array.isArray(applicationIDs) || applicationIDs.length === 0) {
		callback([]);
		return () => {};
	}

	const matches: Record<string, Record<string, unknown>> = {};
	const unsubscribeFunctions = applicationIDs.map((id) => {
		const docRef = doc(db, collections.applications, id);

		return onSnapshot(
			docRef,
			(snapshot) => {
				const data = snapshot.data();

				if (snapshot.exists() && data && data.status !== ApplicationStatus.deleted) {
					matches[snapshot.id] = { id: snapshot.id, ...data };
				} else {
					delete matches[snapshot.id];
				}

				callback(sortApplicationsByLastTouched(Object.values(matches)));
			},
			(error) => {
				logEvent('Error in getRealTimeApplicationsByIDs', error);
			}
		);
	});

	return () => {
		for (const unsub of unsubscribeFunctions) {
			unsub();
		}
	};
};

const normalizeAwardIdList = (awardIDs: unknown[]): string[] =>
	[...new Set(awardIDs.map((id) => String(id || '').trim()).filter(Boolean))];

export const getRealTimeAwardsByIDs = (awardIDs: string[], callback: RealtimeCallback) => {
	const ids = normalizeAwardIdList(Array.isArray(awardIDs) ? awardIDs : []);
	if (ids.length === 0) {
		throw new TypeError('awardIDs must be a non-empty array');
	}

	const matches: Record<string, Record<string, unknown>> = {};
	const unsubscribeFunctions = ids.map((id) => {
		const docRef = doc(db, collections.awards, id);
		return onSnapshot(
			docRef,
			(snapshot) => {
				if (snapshot.exists()) {
					matches[snapshot.id] = { id: snapshot.id, ...snapshot.data() };
				} else {
					delete matches[snapshot.id];
				}
				callback(Object.values(matches));
			},
			(error) => {
				logEvent('Error in getRealTimeAwardsByIDs', error);
			}
		);
	});

	return () => {
		for (const unsub of unsubscribeFunctions) {
			unsub();
		}
	};
};

export const getAwardsData = async (userID: string, awardIDs: string[]) => {
	if (!Array.isArray(awardIDs)) {
		throw new TypeError('awardIDs must be an array');
	}

	try {
		const ids = normalizeAwardIdList(awardIDs);
		const awardPromises = ids.map((appId) => getDoc(doc(db, collections.awards, appId)));
		const awardDocs = await Promise.all(awardPromises);
		const awards = awardDocs.filter((docSnap) => docSnap.exists()).map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
		return awards;
	} catch (error) {
		logEvent('Error in getAwardsData', error);
		throw error;
	}
};

/** Awards linked by applicationID / applicationId (covers docs missing from applications.awards). */
export const getAwardsByApplicationId = async (applicationId: string) => {
	const id = String(applicationId || '').trim();
	if (!id) return [];

	try {
		const awardsRef = collection(db, collections.awards);
		const [byApplicationID, byApplicationId] = await Promise.all([
			getDocs(query(awardsRef, where('applicationID', '==', id))),
			getDocs(query(awardsRef, where('applicationId', '==', id))),
		]);

		const byId = new Map<string, Record<string, unknown>>();
		for (const snap of [...byApplicationID.docs, ...byApplicationId.docs]) {
			if (!byId.has(snap.id)) byId.set(snap.id, { id: snap.id, ...snap.data() });
		}
		return [...byId.values()];
	} catch (error) {
		logEvent('Error in getAwardsByApplicationId', error);
		return [];
	}
};

/** Live awards for an application: linked IDs plus applicationID query fallback. */
export const subscribeToApplicationAwards = (
	applicationId: string | null | undefined,
	awardIDs: unknown,
	callback: (awards: Record<string, unknown>[]) => void,
) => {
	const linkedIds = normalizeAwardIdList(
		Array.isArray(awardIDs)
			? awardIDs.map((entry) => {
					if (typeof entry === 'string') return entry;
					if (entry && typeof entry === 'object') {
						const record = entry as Record<string, unknown>;
						return String(record.id || record.awardID || record.awardId || '');
					}
					return '';
				})
			: [],
	);
	const appId = String(applicationId || '').trim();
	let linked: Record<string, unknown>[] = [];
	let byApplication: Record<string, unknown>[] = [];
	const unsubscribers: Array<() => void> = [];

	const emit = () => {
		const byId = new Map<string, Record<string, unknown>>();
		for (const award of [...linked, ...byApplication]) {
			const id = String(award.id || award.awardID || '').trim();
			const key = id || JSON.stringify(award);
			if (!byId.has(key)) byId.set(key, award);
		}
		callback([...byId.values()]);
	};

	if (linkedIds.length > 0) {
		unsubscribers.push(
			getRealTimeAwardsByIDs(linkedIds, (data) => {
				linked = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
				emit();
			}),
		);
	} else {
		linked = [];
		emit();
	}

	if (appId) {
		const awardsRef = collection(db, collections.awards);
		const mergeQuerySnaps = (snaps: Array<QuerySnapshot | null>) => {
			const byId = new Map<string, Record<string, unknown>>();
			for (const snap of snaps) {
				if (!snap) continue;
				for (const docSnap of snap.docs) {
					if (!byId.has(docSnap.id)) byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
				}
			}
			byApplication = [...byId.values()];
			emit();
		};

		let snapA: QuerySnapshot | null = null;
		let snapB: QuerySnapshot | null = null;
		unsubscribers.push(
			onSnapshot(
				query(awardsRef, where('applicationID', '==', appId)),
				(snapshot) => {
					snapA = snapshot;
					mergeQuerySnaps([snapA, snapB]);
				},
				(error) => {
					logEvent('subscribeToApplicationAwards applicationID error', error);
				},
			),
		);
		unsubscribers.push(
			onSnapshot(
				query(awardsRef, where('applicationId', '==', appId)),
				(snapshot) => {
					snapB = snapshot;
					mergeQuerySnaps([snapA, snapB]);
				},
				(error) => {
					logEvent('subscribeToApplicationAwards applicationId error', error);
				},
			),
		);
	}

	return () => {
		for (const unsub of unsubscribers) unsub();
	};
};

export const getApplicationsByIDs = async (applicationIDs: string[]) => {
	if (!Array.isArray(applicationIDs)) {
		throw new TypeError('applicationIDs must be an array');
	}

	try {
		const applicationPromises = applicationIDs.map((appId) => getDoc(doc(db, collections.applications, appId)));
		const applicationDocs = await Promise.all(applicationPromises);
		const applications = applicationDocs.filter((doc) => doc.exists()).map((doc) => doc.data());
		return applications;
	} catch (error) {
		logEvent('Error in getApplicationsByIDs', error);
		return null;
	}
};

export const getCollectionData = async (userID: string, collectionName: CollectionName, dataID: string) => {
	if (dataID) {
		try {
			const docRef = doc(db, collectionName, dataID);
			const docSnap = await getDoc(docRef);
			return docSnap.exists() ? docSnap.data() : null;
		} catch (error) {
			logEvent('Error in getCollectionData', error);
			return null;
		}
	}
};

const applicantDisplayName = (applicant: DocumentData | undefined, applicantId: string): string => {
	if (!applicant) return applicantId;
	const first = String(applicant.firstName ?? '').trim();
	const last = String(applicant.lastName ?? '').trim();
	const name = `${first} ${last}`.trim();
	return name || String(applicant.email ?? applicantId);
};

const applicantAccountCycleYear = (applicant: DocumentData | undefined): number | null => {
	const created = applicant?.accountCreated;
	if (created && typeof created === 'object' && 'seconds' in created && typeof created.seconds === 'number') {
		return new Date(created.seconds * 1000).getFullYear();
	}
	if (created && typeof created === 'object' && 'toDate' in created && typeof created.toDate === 'function') {
		return created.toDate().getFullYear();
	}
	if (created instanceof Date) return created.getFullYear();
	return null;
};

const appsForApplicant = (applicantId: string, apps: DocumentData[]): DocumentData[] =>
	apps.filter((app) => String(app.completedBy ?? '') === applicantId);

const hasApplicationBeforeCycle = (applicantId: string, cycleYear: number, apps: DocumentData[]): boolean =>
	appsForApplicant(applicantId, apps).some((app) => {
		const year = resolveApplicationCycleYear(app);
		return year !== null && year < cycleYear;
	});

const currentCycleApplicationTypes = (applicantId: string, cycleYear: number, apps: DocumentData[]): ApplicationTypeValue[] => {
	const types = new Set<ApplicationTypeValue>();
	for (const app of appsForApplicant(applicantId, apps)) {
		if (resolveApplicationCycleYear(app) !== cycleYear) continue;
		const type = app.type as string;
		if (isApplicationTypeValue(type)) types.add(type);
	}
	return [...types];
};

/** Distinct cycle years with any awarded application (grant and/or scholarship count as one cycle per year). */
const countUndergraduateAwardCycles = (applicantId: string, apps: DocumentData[]): number => {
	const years = new Set<number>();
	for (const app of appsForApplicant(applicantId, apps)) {
		if (app.status !== ApplicationStatus.awarded) continue;
		const year = resolveApplicationCycleYear(app);
		if (year !== null) years.add(year);
	}
	return years.size;
};

/** Undergrad-only fund: eligible when expected graduation year is still this cycle or later. */
const applicantIsUndergradEligible = (gradYear: unknown, cycleYear: number): boolean => {
	const parsed = parseGradYear(gradYear);
	if (parsed === null) return true;
	return parsed >= cycleYear;
};

const activeAppsForApplicant = (applicantId: string, apps: DocumentData[]): DocumentData[] =>
	appsForApplicant(applicantId, apps).filter((app) => app.status !== ApplicationStatus.deleted);

const hasCompletedApplicationEver = (applicantId: string, apps: DocumentData[]): boolean =>
	activeAppsForApplicant(applicantId, apps).some((app) => SUBMITTED_OR_BEYOND_STATUSES.has(String(app.status)));

const hasAnyAward = (applicantId: string, apps: DocumentData[]): boolean =>
	activeAppsForApplicant(applicantId, apps).some((app) => app.status === ApplicationStatus.awarded);

const hasScholarshipAwardEver = (applicantId: string, apps: DocumentData[]): boolean =>
	activeAppsForApplicant(applicantId, apps).some(
		(app) => app.status === ApplicationStatus.awarded && app.type === ApplicationType.scholarship
	);

const isOutlookEligible = (applicantId: string, applicant: DocumentData | undefined, cycleYear: number, apps: DocumentData[]): boolean => {
	if (!applicantIsUndergradEligible(applicant?.gradYear, cycleYear)) return false;
	if (countUndergraduateAwardCycles(applicantId, apps) >= MAX_UNDERGRAD_AWARD_CYCLES) return false;
	return true;
};

const isCurrentCycleAccount = (applicant: DocumentData | undefined, cycleYear: number): boolean => {
	const accountYear = applicantAccountCycleYear(applicant);
	return accountYear === null || accountYear >= cycleYear;
};

const isLostInTheWeeds = (
	applicantId: string,
	applicant: DocumentData | undefined,
	cycleYear: number,
	apps: DocumentData[]
): boolean => {
	if (hasCompletedApplicationEver(applicantId, apps)) return false;
	if (isCurrentCycleAccount(applicant, cycleYear) && !hasApplicationBeforeCycle(applicantId, cycleYear, apps)) return false;
	return true;
};

const lastAwardedApplicationMatching = (
	applicantId: string,
	apps: DocumentData[],
	types: Set<string>
): { type: string; cycleYear: number } | null => {
	let latest: { type: string; cycleYear: number } | null = null;
	for (const app of activeAppsForApplicant(applicantId, apps)) {
		if (app.status !== ApplicationStatus.awarded || !types.has(String(app.type))) continue;
		const year = resolveApplicationCycleYear(app);
		if (year === null) continue;
		if (!latest || year > latest.cycleYear) {
			latest = { type: String(app.type), cycleYear: year };
		}
	}
	return latest;
};

const resolveOutlookSectionId = (
	applicantId: string,
	applicant: DocumentData | undefined,
	cycleYear: number,
	apps: DocumentData[]
): OutlookSectionId | null => {
	if (!isOutlookEligible(applicantId, applicant, cycleYear, apps)) return null;

	if (hasScholarshipAwardEver(applicantId, apps)) return 'expectedReturningScholarships';
	if (hasAnyAward(applicantId, apps)) return 'expectedReturningGrants';

	const currentTypes = currentCycleApplicationTypes(applicantId, cycleYear, apps);
	const hasAnyCurrentCycleApp = currentTypes.length > 0;
	const isNewThisCycle = isCurrentCycleAccount(applicant, cycleYear) && !hasApplicationBeforeCycle(applicantId, cycleYear, apps);

	if (isNewThisCycle && !hasAnyCurrentCycleApp) return 'brandNewAccounts';
	if (isLostInTheWeeds(applicantId, applicant, cycleYear, apps)) return 'lostInTheWeeds';

	return 'expectedNewApplications';
};

const resolveCycleStatus = (
	expectedType: ApplicationTypeValue | null,
	currentTypes: ApplicationTypeValue[]
): OutlookRowCycleStatus => {
	if (!expectedType) return 'none';
	if (currentTypes.length === 0) return 'none';
	return currentTypes.includes(expectedType) ? 'correct' : 'wrong';
};

const resolveAwardDisplay = (
	applicantId: string,
	apps: DocumentData[],
	sectionId: OutlookSectionId
): { display: string; lastAwardType: string | null; lastCycleYear: number | null } => {
	if (sectionId === 'expectedReturningScholarships') {
		const last = lastAwardedApplicationMatching(applicantId, apps, new Set([ApplicationType.scholarship]));
		if (last) return { display: `${last.type} (${last.cycleYear})`, lastAwardType: last.type, lastCycleYear: last.cycleYear };
	}
	if (sectionId === 'expectedReturningGrants') {
		const last = lastAwardedApplicationMatching(
			applicantId,
			apps,
			new Set([ApplicationType.returningGrant, ApplicationType.newApplication])
		);
		if (last) return { display: `${last.type} (${last.cycleYear})`, lastAwardType: last.type, lastCycleYear: last.cycleYear };
	}

	if (activeAppsForApplicant(applicantId, apps).length === 0) {
		return { display: 'Account Created', lastAwardType: null, lastCycleYear: null };
	}
	return { display: 'First Time Applicant', lastAwardType: null, lastCycleYear: null };
};

const outlookSectionLink = (pathKey: (typeof OUTLOOK_PIPELINE_SECTIONS)[number]['pathKey'], cycleYear: number): string => {
	if (pathKey === 'applicants') return '/applicants/all';
	if (pathKey === 'newAppsInYear') return `/applications/${cycleYear}/newApplicants`;
	if (pathKey === 'returningAppsInYear') return `/applications/${cycleYear}/returningGrants`;
	return `/applications/${cycleYear}/scholarshipRecipients`;
};

const toOutlookRow = (
	applicantId: string,
	applicant: DocumentData | undefined,
	cycleYear: number,
	apps: DocumentData[],
	sectionId: OutlookSectionId,
	expectedType: ApplicationTypeValue | null
): DashboardApplicantOutlookRow => {
	const gradYearParsed = normalizeGradYear(applicant?.gradYear);
	const gradYear = gradYearParsed === null ? null : String(gradYearParsed);
	const currentTypes = currentCycleApplicationTypes(applicantId, cycleYear, apps);
	const award = resolveAwardDisplay(applicantId, apps, sectionId);

	return {
		applicantId,
		name: applicantDisplayName(applicant, applicantId),
		gradYear,
		awardDisplay: award.display,
		lastAwardType: award.lastAwardType,
		lastCycleYear: award.lastCycleYear,
		cycleStatus: resolveCycleStatus(expectedType, currentTypes),
	};
};

const sortOutlookRows = (rows: DashboardApplicantOutlookRow[]): DashboardApplicantOutlookRow[] =>
	[...rows].sort((a, b) => {
		const yearDiff = (b.lastCycleYear ?? 0) - (a.lastCycleYear ?? 0);
		if (yearDiff !== 0) return yearDiff;
		return a.name.localeCompare(b.name);
	});

const isOutlookSectionComplete = (applicants: DashboardApplicantOutlookRow[]): boolean =>
	applicants.length > 0 && applicants.every((row) => row.cycleStatus === 'correct');

export const buildDashboardApplicantOutlook = (
	apps: DocumentData[],
	applicantsById: Map<string, DocumentData>,
	cycleYear: number
): DashboardApplicantOutlook => {
	const sectionRows = new Map<OutlookSectionId, DashboardApplicantOutlookRow[]>(
		OUTLOOK_PIPELINE_SECTIONS.map(({ id }) => [id, []])
	);

	for (const [applicantId, applicant] of applicantsById.entries()) {
		const sectionId = resolveOutlookSectionId(applicantId, applicant, cycleYear, apps);
		if (!sectionId) continue;
		const meta = OUTLOOK_PIPELINE_SECTIONS.find((section) => section.id === sectionId);
		if (!meta) continue;
		sectionRows.get(sectionId)?.push(toOutlookRow(applicantId, applicant, cycleYear, apps, sectionId, meta.expectedType));
	}

	const sections: DashboardApplicantOutlookSection[] = OUTLOOK_PIPELINE_SECTIONS.map(({ id, title, pathKey, expectedType }) => {
		const applicants = sortOutlookRows(sectionRows.get(id) ?? []);
		if (id === 'brandNewAccounts' || id === 'lostInTheWeeds') {
			applicants.sort((a, b) => a.name.localeCompare(b.name));
		}
		return {
			id,
			title,
			link: outlookSectionLink(pathKey, cycleYear),
			expectedType,
			applicants,
			isComplete: isOutlookSectionComplete(applicants),
		};
	});

	return { cycleYear, sections };
};

export const getRealTimeDashboardApplicantOutlook = (callback: RealtimeCallback<DashboardApplicantOutlook>) => {
	let cycleYear = new Date().getFullYear();
	let apps: DocumentData[] = [];
	let applicantsById = new Map<string, DocumentData>();
	let configLoaded = false;

	const publish = () => {
		if (!configLoaded) return;
		try {
			callback(buildDashboardApplicantOutlook(apps, applicantsById, cycleYear));
		} catch (error) {
			logEvent('buildDashboardApplicantOutlook publish error', error);
		}
	};

	void getConfigFromDb()
		.then((config) => {
			cycleYear = siteConfigCycleYear(config);
			configLoaded = true;
			publish();
		})
		.catch((error) => {
			logEvent('Error loading config for getRealTimeDashboardApplicantOutlook', error);
			configLoaded = true;
			publish();
		});

	try {
		const unsubApps = onSnapshot(
			collection(db, collections.applications),
			(snapshot) => {
				apps = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
				publish();
			},
			(error) => {
				logEvent('getRealTimeDashboardApplicantOutlook applications error', error);
			}
		);

		const unsubApplicants = onSnapshot(
			collection(db, collections.applicants),
			(snapshot) => {
				applicantsById = new Map(snapshot.docs.map((docSnap) => [docSnap.id, docSnap.data()]));
				publish();
			},
			(error) => {
				logEvent('getRealTimeDashboardApplicantOutlook applicants error', error);
			}
		);

		return () => {
			unsubApps();
			unsubApplicants();
		};
	} catch (error) {
		logEvent('Error in getRealTimeDashboardApplicantOutlook', error);
		return null;
	}
};

export const getDashboardBenchmarkData = async (cycleYear: number): Promise<DashboardBenchmarkData> => {
	const priorYear = cycleYear - 1;
	const trendYears = [cycleYear - 3, cycleYear - 2, cycleYear - 1];
	const pipelineStatusSet = new Set<string>(PIPELINE_STATUSES);

	const currentCounts = emptyTypeCounts();
	const benchmarkTargets = emptyTypeCounts();
	const returningPool = new Set<string>();
	const scholarshipPool = new Set<string>();
	const awardTrendMap: Record<number, AwardTrendYear> = {};
	for (const year of trendYears) {
		awardTrendMap[year] = emptyAwardTrend(year);
	}

	try {
		const [appsSnapshot, applicantsSnapshot] = await Promise.all([
			getDocs(collection(db, collections.applications)),
			getDocs(collection(db, collections.applicants)),
		]);

		const applicantsById = new Map(applicantsSnapshot.docs.map((docSnap) => [docSnap.id, docSnap.data()]));

		for (const docSnap of appsSnapshot.docs) {
			const app = docSnap.data();
			const appYear = resolveApplicationCycleYear(app);
			if (appYear === null) continue;

			const type = app.type as string;
			const status = app.status as string;
			const applicantId = app.completedBy ? String(app.completedBy) : '';

			if (appYear === cycleYear && pipelineStatusSet.has(status)) {
				if (type === ApplicationType.newApplication) currentCounts[ApplicationType.newApplication]++;
				else if (type === ApplicationType.returningGrant) currentCounts[ApplicationType.returningGrant]++;
				else if (type === ApplicationType.scholarship) currentCounts[ApplicationType.scholarship]++;
			}

			if (appYear === priorYear && pipelineStatusSet.has(status) && type === ApplicationType.newApplication) {
				benchmarkTargets[ApplicationType.newApplication]++;
			}

			if (status === ApplicationStatus.awarded && trendYears.includes(appYear) && applicantId) {
				incrementAwardTrend(awardTrendMap, appYear, type);

				const applicant = applicantsById.get(applicantId);
				if (applicantHasNotGraduated(applicant?.gradYear, cycleYear)) {
					if (type === ApplicationType.returningGrant) returningPool.add(applicantId);
					else if (type === ApplicationType.scholarship) scholarshipPool.add(applicantId);
				}
			}
		}

		benchmarkTargets[ApplicationType.returningGrant] = returningPool.size;
		benchmarkTargets[ApplicationType.scholarship] = scholarshipPool.size;

		return {
			currentCounts,
			benchmarkTargets,
			awardTrends: trendYears.map((year) => awardTrendMap[year] ?? emptyAwardTrend(year)),
		};
	} catch (error) {
		logEvent('Error in getDashboardBenchmarkData', error);
		return {
			currentCounts: emptyTypeCounts(),
			benchmarkTargets: emptyTypeCounts(),
			awardTrends: trendYears.map((year) => emptyAwardTrend(year)),
		};
	}
};

export const getBenchmarkedAwardCounts = async (cycleYear: number) => {
	const data = await getDashboardBenchmarkData(cycleYear + 1);
	return data.benchmarkTargets;
};

export const getAwardCountsByYear = async (): Promise<Record<string, { New: number; Returning: number; Scholarship: number }>> => {
	const config = await getConfigFromDb();
	const cycleYear = siteConfigCycleYear(config);
	const data = await getDashboardBenchmarkData(cycleYear);

	const sorted: Record<string, { New: number; Returning: number; Scholarship: number }> = {};
	for (const entry of data.awardTrends) {
		sorted[String(entry.year)] = {
			New: entry.New,
			Returning: entry.Returning,
			Scholarship: entry.Scholarship,
		};
	}
	return sorted;
};

export const getRequestData = async (dataID: string) => {
	if (dataID) {
		try {
			const docRef = doc(db, collections.requests, dataID);
			const docSnap = await getDoc(docRef);
			return docSnap.exists() ? docSnap.data() : null;
		} catch (error) {
			logEvent('Error in getRequestData', error);
			return null;
		}
	}
};

export const invalidateRequest = async (dataID: string) => {
	try {
		const newRef = doc(db, collections.requests, dataID);
		await updateDoc(newRef, { expiryDate: new Date(Date.now() - 1000).toLocaleString() });
		return true;
	} catch (error) {
		logEvent('Error in invalidateRequest', error);
		return false;
	}
};

export const deleteApplication = async (application: { id: string }) => {
	try {
		const submissionRef = doc(db, collections.applications, application.id);
		await updateDoc(submissionRef, {
			status: ApplicationStatus.deleted,
			deletedOn: new Date(),
		});
		return true;
	} catch (error) {
		logEvent('Error in deleteApplication', error);
		return false;
	}
};

export const permanentlyDeleteApplication = async ({ applicationId }: PermanentlyDeleteApplicationParams) => {
	if (!applicationId) {
		throw new Error('Application ID is required.');
	}

	const result = await permanentlyDeleteApplicationCallable({ applicationId });
	return (result.data as { message?: string }) ?? { message: `Application ${applicationId} permanently deleted.` };
};

export const removeApplicationFromApplicant = async (userId: string, applicationId: string) => {
	try {
		const applicantRef = doc(db, collections.applicants, userId);
		await updateDoc(applicantRef, { applications: arrayRemove(applicationId) });
		return true;
	} catch (error) {
		logEvent('Error in removingApplicationFromApplicant', error);
		return false;
	}
};

export const getAuthUserByEmail = async (email: string) => {
	try {
		const usersRef = collection(db, collections.users);
		const q = query(usersRef, where('email', '==', email));
		const querySnapshot = await getDocs(q);
		return querySnapshot.empty ? null : querySnapshot.docs[0].data();
	} catch (error) {
		logEvent('Error in getAuthUserByEmail', error);
		return null;
	}
};

/**
 * Fetches both member and applicant profiles for a given user ID.
 * @param {string} uid - The user's ID.
 * @returns {object} An object containing member and applicant data if they exist.
 */
export const getUserProfiles = async (uid: string) => {
	if (uid) {
		try {
			const memberRef = doc(db, collections.members, uid);
			const applicantRef = doc(db, collections.applicants, uid);

			const [memberSnap, applicantSnap] = await Promise.all([getDoc(memberRef), getDoc(applicantRef)]);

			return {
				member: memberSnap.exists() ? memberSnap.data() : null,
				applicant: applicantSnap.exists() ? applicantSnap.data() : null,
			};
		} catch (error) {
			logEvent('Error in getUserProfiles', error);
			return { member: null, applicant: null };
		}
	}
	return null;
};

export const registerUser = async (email: string, password: string) => {
	try {
		const user = await createUserWithEmailAndPassword(auth, email, password);
		return user;
	} catch (error) {
		logEvent('Error in registerUser', error);
		throw error;
	}
};

export const loginUser = async (email: string, password: string) => {
	try {
		const user = await signInWithEmailAndPassword(auth, email, password);
		return user;
	} catch (error) {
		logEvent('Error in loginUser', error);
		throw error;
	}
};

export const logoutUser = async () => {
	await signOut(auth);
};

export const saveFile = async (type: string, assetID: string, name: string, file: Blob) => {
	if (file == null || assetID == null) {
		throw new Error('No file or asset included in request.');
	}
	let filename = `${uuid()}_${name || ''}`;
	const fileRef = ref(storage, `${type}/${assetID}/${filename}`);
	try {
		const snapshot = await uploadBytes(fileRef, file);
		return snapshot.ref.fullPath;
	} catch (error) {
		logEvent('Error in saveFile', error);
		throw error;
	}
};

export const deleteFile = async (location: string) => {
	if (!location) {
		return false;
	}

	const fileRef = ref(storage, location);

	try {
		await deleteObject(fileRef);
		return true;
	} catch (error) {
		logEvent('Error in deleteFile', error);
		return false;
	}
};

export const getFile = async (refLoc: string) => {
	if (!refLoc) throw new Error('Missing refLoc path');
	const storageRef = ref(storage, refLoc);
	const url = await getDownloadURL(storageRef);
	return url;
};

export const getDownloadLinkForFile = async (file: string | { refLoc?: string } | null) => {
	if (file == null) {
		return null;
	}

	const refLoc = typeof file === 'string' ? file : file.refLoc;
	if (!refLoc) {
		return null;
	}

	const directoryRef = ref(storage, refLoc);

	try {
		const url = await getDownloadURL(directoryRef);
		return url;
	} catch (error) {
		logEvent('Error in getDownloadLinkForFile', error);
		return null;
	}
};

export const updateSubmissionStatus = async (submissionID: string, status: string) => {
	try {
		const submissionRef = doc(db, collections.applications, submissionID);
		await updateDoc(submissionRef, {
			status: status,
			updatedOn: new Date(),
		});
		return true;
	} catch (error) {
		logEvent('Error updating submission status', error);
		return false;
	}
};

const processSnapshotForBatchDelete = (
	snapshot: QuerySnapshot<DocumentData>,
	collectionName: CollectionName,
	batch: WriteBatch,
	result: { count: number; applicationIds: string[]; filePromises: Promise<unknown>[] }
) => {
	if (snapshot.empty) {
		return;
	}

	result.count += snapshot.size;
	for (const docSnap of snapshot.docs) {
		batch.delete(docSnap.ref);

		if (collectionName === collections.applications) {
			result.applicationIds.push(docSnap.id);
		}

		if (collectionName === collections.attachments) {
			const docData = docSnap.data();
			for (const value of Object.values(docData)) {
				if (value && typeof value === 'object' && 'refLoc' in value && typeof value.refLoc === 'string') {
					result.filePromises.push(deleteFile(value.refLoc as string));
				}
			}
		}
	}
};

const batchDeleteOwnedDocuments = async (userId: string, batch: WriteBatch) => {
	const result = {
		count: 0,
		applicationIds: [] as string[],
		filePromises: [] as Promise<unknown>[],
	};

	for (const collectionName of applicationSpecificCollections) {
		const q = query(collection(db, collectionName), where('completedBy', '==', userId));
		const snapshot = await getDocs(q);

		processSnapshotForBatchDelete(snapshot, collectionName, batch, result);
	}
	return result;
};

const batchDeleteRelatedRequests = async (applicationIds: string[], batch: WriteBatch) => {
	if (!applicationIds || applicationIds.length === 0) {
		return 0;
	}
	const requestsQuery = query(collection(db, collections.requests), where('applicationID', 'in', applicationIds));
	const requestsSnapshot = await getDocs(requestsQuery);

	if (requestsSnapshot.empty) {
		return 0;
	}

	for (const docSnap of requestsSnapshot.docs) {
		batch.delete(docSnap.ref);
	}
	return requestsSnapshot.size;
};

export const purgeUserRecords = async ({ userId, expel = false }: PurgeUserRecordsParams) => {
	if (!userId) {
		throw new Error('User ID is required to purge records.');
	}

	const batch = writeBatch(db);
	let deletedDocsCount = 0;

	const ownedDocsResult = await batchDeleteOwnedDocuments(userId, batch);
	deletedDocsCount += ownedDocsResult.count;
	await Promise.all(ownedDocsResult.filePromises);

	const deletedRequestsCount = await batchDeleteRelatedRequests(ownedDocsResult.applicationIds, batch);
	deletedDocsCount += deletedRequestsCount;

	if (expel) {
		batch.delete(doc(db, collections.applicants, userId));
		batch.delete(doc(db, collections.users, userId));
		deletedDocsCount += 2;
	} else {
		batch.update(doc(db, collections.applicants, userId), { applications: [] });
	}

	await batch.commit();

	if (expel) {
		const deleteAuthUser = httpsCallable(functions, 'deleteAuthUser');
		await deleteAuthUser({ uid: userId });
	}

	return { message: `Successfully purged ${deletedDocsCount} records for user ${userId}. This user was ${expel ? '' : 'not'} expelled.` };
};

const getParentNameForNote = async (parentRef: DocumentReference<DocumentData>) => {
	try {
		const parentDocSnap = await getDoc(parentRef);
		if (!parentDocSnap.exists()) return parentRef.id;

		const parentData = parentDocSnap.data() as Record<string, unknown>;
		const parentCollection = parentRef.path.split('/')[0];

		if (parentCollection === collections.applicants) {
			return `${String(parentData.firstName ?? '')} ${String(parentData.lastName ?? '')}`;
		}

		if (parentCollection === collections.applications) {
			let appHolderName = 'Unknown Applicant';
			if (parentData.completedBy) {
				const applicantRef = doc(db, collections.applicants, String(parentData.completedBy));
				const applicantSnap = await getDoc(applicantRef);
				if (applicantSnap.exists()) {
					const applicant = applicantSnap.data() as Record<string, unknown>;
					appHolderName = `${String(applicant.firstName ?? '')} ${String(applicant.lastName ?? '')}`;
				}
			}
			const appYear = new Date(String(parentData.window ?? '')).getFullYear();
			return `${appHolderName}'s ${String(parentData.status ?? '')} ${String(parentData.type ?? '')} (${appYear})`;
		}

		return parentRef.id;
	} catch (error) {
		logEvent('Error in getParentNameForNote', error);
		return parentRef.id;
	}
};

export const getNotesByAuthor = async (authorId: string) => {
	if (!authorId) return [];

	const notesQuery = query(collectionGroup(db, 'notes'), where('authorId', '==', authorId), orderBy('createdAt', 'desc'));
	const snapshot = await getDocs(notesQuery);

	const userNotesPromises = snapshot.docs.map(async (noteDoc) => {
		const data = noteDoc.data();
		const parentRef = noteDoc.ref.parent.parent;
		if (!parentRef) {
			return {
				id: noteDoc.id,
				...data,
				parent: null,
			};
		}
		const parentName = await getParentNameForNote(parentRef);

		return {
			id: noteDoc.id,
			...data,
			parent: {
				id: parentRef.id,
				collection: parentRef.path.split('/')[0],
				name: parentName,
			},
		};
	});

	return Promise.all(userNotesPromises);
};

export const getRealTimeNotes = (targetCollection: CollectionName, targetId: string, callback: RealtimeCallback) => {
	const auth = getAuth();
	const currentUser = auth.currentUser;

	if (!currentUser) return () => {};

	const notesRef = collection(db, targetCollection, targetId, 'notes');

	const q = query(notesRef, or(where('visibility', '==', 'committee'), where('authorId', '==', currentUser.uid)), orderBy('createdAt', 'desc'));

	const unsubscribe = onSnapshot(q, (snapshot) => {
		const notes = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
		callback(notes);
	});

	return unsubscribe;
};

export const addNote = async (targetCollection: CollectionName, targetId: string, noteData: Record<string, unknown>) => {
	const notesRef = collection(db, targetCollection, targetId, 'notes');
	await addDoc(notesRef, {
		...noteData,
		createdAt: new Date(),
		updatedAt: new Date(),
	});
};

export const updateNote = async (targetCollection: CollectionName, targetId: string, noteId: string, newText: string) => {
	const noteRef = doc(db, targetCollection, targetId, 'notes', noteId);
	await updateDoc(noteRef, {
		text: newText,
		updatedAt: new Date(),
	});
};

export const redactNote = async (targetCollection: CollectionName, targetId: string, noteId: string) => {
	const noteRef = doc(db, targetCollection, targetId, 'notes', noteId);
	await updateDoc(noteRef, {
		redacted: true,
		redactedOn: new Date(),
	});
};

export const getMeetings = async (userId: string, isCommittee: boolean) => {
	const interviewsRef = collection(db, 'interviews');
	let q;

	const relevantStatuses = [InterviewStatus.scheduled, InterviewStatus.invited, InterviewStatus.confirmed, InterviewStatus.inProgress, InterviewStatus.completed];

	if (isCommittee) {
		q = query(interviewsRef, where('status', 'in', relevantStatuses), orderBy('startTime', 'asc'));
	} else {
		q = query(interviewsRef, where('applicantId', '==', userId), where('status', 'in', relevantStatuses));
	}

	const querySnapshot = await getDocs(q);
	const meetings = await Promise.all(
		querySnapshot.docs.map(async (document) => {
			const meeting: MeetingRecord = { id: document.id, ...document.data() };

			if (meeting.applicantId) {
				try {
					const applicantRef = doc(db, collections.applicants, meeting.applicantId);
					const applicantDoc = await getDoc(applicantRef);
					if (applicantDoc.exists()) {
						const applicant = applicantDoc.data();
						meeting.displayName = `${applicant.callMe} | ${applicant.firstName} ${applicant.lastName}`;
					} else {
						meeting.displayName = 'Unknown Applicant';
					}
				} catch (error) {
					logEvent('Error in getMeetings', error);
					meeting.displayName = 'Error fetching name';
				}
			}
			return meeting;
		})
	);

	if (isCommittee) {
		meetings.push({
			id: 'deliberation',
			deliberation: true,
			displayName: 'Committee Deliberation',
		} as MeetingRecord);
	}

	return meetings;
};

export const getRealTimeMeetings = (userId: string, isCommittee: boolean, callback: RealtimeCallback, isDash = false) => {
	const interviewsRef = collection(db, 'interviews');
	let q;
	const relevantStatuses = isDash
		? [InterviewStatus.scheduled, InterviewStatus.invited, InterviewStatus.confirmed, InterviewStatus.inProgress]
		: [InterviewStatus.scheduled, InterviewStatus.invited, InterviewStatus.confirmed, InterviewStatus.inProgress, InterviewStatus.completed, InterviewStatus.missed];

	if (isCommittee) {
		q = query(interviewsRef, where('status', 'in', relevantStatuses), orderBy('startTime', 'asc'));
	} else {
		q = query(interviewsRef, where('applicantId', '==', userId), where('status', 'in', relevantStatuses));
	}

	const unsubscribe = onSnapshot(q, async (querySnapshot) => {
		const meetings = await Promise.all(
			querySnapshot.docs.map(async (document) => {
				const meeting: MeetingRecord = { id: document.id, ...document.data() };
				if (meeting.applicantId) {
					try {
						const applicantRef = doc(db, collections.applicants, meeting.applicantId);
						const applicantDoc = await getDoc(applicantRef);
						if (applicantDoc.exists()) {
							const applicant = applicantDoc.data();
							meeting.displayName = `${applicant.callMe} | ${applicant.firstName} ${applicant.lastName}`;
						}
					} catch (error) {
						logEvent(`Error fetching applicant name for interview ${meeting.id}`, error);
					}
				}
				return meeting;
			})
		);
		callback(meetings);
	});
	return unsubscribe;
};

export const getSchedulableInterviews = async () => {
	const interviewsRef = collection(db, 'interviews');
	const q = query(interviewsRef, where('status', '==', 'scheduled'), where('roomId', '==', null));
	const querySnapshot = await getDocs(q);
	const interviews = [];
	for (const doc of querySnapshot.docs) {
		interviews.push({ id: doc.id, ...doc.data() });
	}
	return interviews;
};

export const getInterviewsByWindow = async (cycleYear: number) => {
	const interviewsRef = collection(db, 'interviews');
	const snapshot = await getDocs(interviewsRef);
	const cycleInterviews = snapshot.docs
		.map((details) => ({ id: details.id, ...details.data() } as InterviewRecord))
		.filter((interview) => resolveInterviewCycleYear(interview) === cycleYear)
		.sort((a, b) => interviewStartTime(a) - interviewStartTime(b));

	if (cycleInterviews.length === 0) {
		return [];
	}

	const interviewsWithNames = await Promise.all(
		cycleInterviews.map(async (interview) => {
			try {
				const applicantDoc = await getDoc(doc(db, collections.applicants, interview.applicantId ?? ''));

				if (applicantDoc.exists()) {
					const applicant = applicantDoc.data();
					interview.applicantName = `${applicant.firstName} ${applicant.lastName}`;
				} else {
					interview.applicantName = 'Unknown Applicant';
				}
			} catch {
				interview.applicantName = 'Error fetching name';
			}
			return interview;
		})
	);

	return interviewsWithNames;
};

export const getRealTimeInterviewsByWindow = (cycleYear: number, callback: RealtimeCallback) => {
	const interviewsRef = collection(db, 'interviews');

	const unsubscribe = onSnapshot(
		interviewsRef,
		async (snapshot) => {
			const cycleInterviews = snapshot.docs
				.map((details) => ({ id: details.id, ...details.data() } as InterviewRecord))
				.filter((interview) => resolveInterviewCycleYear(interview) === cycleYear)
				.sort((a, b) => interviewStartTime(a) - interviewStartTime(b));

			if (cycleInterviews.length === 0) {
				callback([]);
				return;
			}

			const interviewsWithNames = await Promise.all(
				cycleInterviews.map(async (interview) => {
					try {
						const applicantDoc = await getDoc(doc(db, collections.applicants, interview.applicantId ?? ''));
						if (applicantDoc.exists()) {
							const applicant = applicantDoc.data();
							interview.applicantName = `${applicant.firstName} ${applicant.lastName}`;
						} else {
							interview.applicantName = 'Unknown Applicant';
						}
					} catch {
						interview.applicantName = 'Error fetching name';
					}
					return interview;
				})
			);

			callback(interviewsWithNames);
		},
		(error) => {
			console.error('getRealTimeInterviewsByWindow:', error);
			callback([]);
		}
	);

	return unsubscribe;
};

export const getAllApplicantsSimple = async () => {
	const applicantsRef = collection(db, collections.applicants);
	const q = query(applicantsRef, orderBy('lastName', 'asc'));
	const snapshot = await getDocs(q);

	return snapshot.docs.map((doc) => ({
		id: doc.id,
		name: `${doc.data().firstName} ${doc.data().lastName}`,
	}));
};

export const getApplicationsForApplicant = async (
	applicantId: string,
	statuses: ApplicationStatusValue[] | null = [ApplicationStatus.eligible, ApplicationStatus.completed],
) => {
	const appsRef = collection(db, collections.applications);
	const q =
		statuses === null
			? query(appsRef, where('completedBy', '==', applicantId))
			: query(appsRef, where('completedBy', '==', applicantId), where('status', 'in', statuses));
	const snapshot = await getDocs(q);
	return snapshot.docs
		.map((doc) => ({ id: doc.id, ...doc.data() }))
		.filter((app) => (app as DocumentData).status !== ApplicationStatus.deleted);
};

export const generateICSDownloadURL = async (interview: InterviewICSInput) => {
	const path = `interview-invites/${interview.id}.ics`;
	const fileRef = ref(storage, path);

	try {
		return await getDownloadURL(fileRef);
	} catch (err) {
		if (err && typeof err === 'object' && 'code' in err && err.code === 'storage/object-not-found') {
			const ensureICS = httpsCallable(functions, 'ensureICSFile');
			const fields = getInterviewICSFields(interview);
			const { startTime, endTime, title, description } = fields;
			const start = firestoreValueToDate(startTime);
			const end = endTime ? firestoreValueToDate(endTime) : new Date(start.getTime() + 30 * 60 * 1000);
			const result = await ensureICS({
				interviewId: interview.id,
				startTime: start,
				endTime: end,
				title: typeof title === 'string' ? title : 'Parker Fund Interview',
				description: typeof description === 'string' ? description : 'Scheduled interview session',
				url: `${globalThis.location.origin}/interviews/waiting-room/${interview.id}`,
			});

			return (result.data as { downloadUrl: string }).downloadUrl;
		} else {
			logEvent('Error in generateICSDownloadURL', err);
			throw err;
		}
	}
};

export const backfillApplicantCreationDates = async () => {
	try {
		const backfillFunction = httpsCallable(functions, 'backfillApplicantCreationDates');
		const result = await backfillFunction();
		return result.data as Record<string, unknown>;
	} catch (error) {
		logEvent('Error in backfillApplicantCreationDates', error);
		throw error;
	}
};

export const getRealTimeNewApplicantsThisYear = (callback: RealtimeCallback, limitCount = 5) => {
	const currentYear = new Date().getFullYear();
	const startOfYear = new Date(`${currentYear}-01-01T00:00:00.000Z`);
	const applicantsRef = collection(db, collections.applicants);
	// We want to sort them descending by accountCreated. 
	// Make sure we handle potential missing fields gracefully or create an index if required.
	const q = query(
		applicantsRef, 
		where('accountCreated', '>=', startOfYear),
		orderBy('accountCreated', 'desc'), 
		limit(limitCount)
	);

	const unsubscribe = onSnapshot(q, (snapshot) => {
		const newApplicants = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
		callback(newApplicants);
	}, (error) => {
		console.error("Error fetching new applicants:", error);
		callback([]);
	});

	return unsubscribe;
};

export const migrateDeadlinesToCycleYear = async () => {
	const results = { applications: 0, awards: 0, interviews: 0, siteConfig: false, errors: [] as string[] };

	const parseCycleYear = (val: unknown): number | null => {
		if (typeof val === 'number') return val;
		if (typeof val === 'string') {
			const d = new Date(val);
			return isNaN(d.getTime()) ? null : d.getFullYear();
		}
		if (val && typeof val === 'object' && 'toDate' in val) {
			return (val as { toDate: () => Date }).toDate().getFullYear();
		}
		return null;
	};

	const parseDeadlineDate = (val: unknown): Date | null => {
		if (val instanceof Date) return val;
		if (typeof val === 'string') {
			const d = new Date(val);
			return isNaN(d.getTime()) ? null : d;
		}
		if (val && typeof val === 'object' && 'toDate' in val) {
			return (val as { toDate: () => Date }).toDate();
		}
		return null;
	};

	const batchSize = 450;
	let batch = writeBatch(db);
	let batchCount = 0;

	const commitBatch = async () => {
		if (batchCount > 0) {
			await batch.commit();
			batch = writeBatch(db);
			batchCount = 0;
		}
	};

	try {
		const appsSnapshot = await getDocs(collection(db, collections.applications));
		for (const docSnap of appsSnapshot.docs) {
			const data = docSnap.data();
			const update: UpdateData<DocumentData> = {};

			if (data.cycleYear === undefined) {
				const year = parseCycleYear(data.window);
				if (year) update.cycleYear = year;
			}
			if (data.deadline === undefined || typeof data.deadline === 'string') {
				const d = parseDeadlineDate(data.deadline ?? data.window);
				if (d) update.deadline = d;
			}

			if (Object.keys(update).length > 0) {
				batch.update(doc(db, collections.applications, docSnap.id), update);
				batchCount++;
				results.applications++;
				if (batchCount >= batchSize) await commitBatch();
			}
		}
		await commitBatch();
	} catch (e) {
		results.errors.push(`Applications: ${e instanceof Error ? e.message : String(e)}`);
	}

	try {
		const awardsSnapshot = await getDocs(collection(db, collections.awards));
		for (const docSnap of awardsSnapshot.docs) {
			const data = docSnap.data();
			const update: UpdateData<DocumentData> = {};

			if (data.cycleYear === undefined) {
				const year = parseCycleYear(data.deadline);
				if (year) update.cycleYear = year;
			}
			if (typeof data.deadline === 'string') {
				const d = parseDeadlineDate(data.deadline);
				if (d) update.deadline = d;
			}

			if (Object.keys(update).length > 0) {
				batch.update(doc(db, collections.awards, docSnap.id), update);
				batchCount++;
				results.awards++;
				if (batchCount >= batchSize) await commitBatch();
			}
		}
		await commitBatch();
	} catch (e) {
		results.errors.push(`Awards: ${e instanceof Error ? e.message : String(e)}`);
	}

	try {
		const interviewsSnapshot = await getDocs(collection(db, 'interviews'));
		for (const docSnap of interviewsSnapshot.docs) {
			const data = docSnap.data();
			const update: UpdateData<DocumentData> = {};

			if (data.cycleYear === undefined) {
				const year = parseCycleYear(data.deadline);
				if (year) update.cycleYear = year;
			}

			if (Object.keys(update).length > 0) {
				batch.update(doc(db, 'interviews', docSnap.id), update);
				batchCount++;
				results.interviews++;
				if (batchCount >= batchSize) await commitBatch();
			}
		}
		await commitBatch();
	} catch (e) {
		results.errors.push(`Interviews: ${e instanceof Error ? e.message : String(e)}`);
	}

	try {
		const configDoc = await getDoc(doc(db, collections.siteConfig, CONFIG_DOC_ID));
		if (configDoc.exists()) {
			const configData = configDoc.data();
			const update: UpdateData<DocumentData> = {};

			if (configData.CYCLE_YEAR === undefined) {
				const year = parseCycleYear(configData.APPLICATION_DEADLINE);
				if (year) update.CYCLE_YEAR = year;
			}

			if (Object.keys(update).length > 0) {
				await updateDoc(doc(db, collections.siteConfig, CONFIG_DOC_ID), update);
				results.siteConfig = true;
			}
		}
	} catch (e) {
		results.errors.push(`SiteConfig: ${e instanceof Error ? e.message : String(e)}`);
	}

	return results;
};

export const backfillApplicantGradYears = async () => {
	const results = {
		updated: 0,
		skipped: 0,
		errors: [] as string[],
		updates: [] as { id: string; from: unknown; to: number }[],
	};

	const batchSize = 450;
	let batch = writeBatch(db);
	let batchCount = 0;

	const commitBatch = async () => {
		if (batchCount > 0) {
			await batch.commit();
			batch = writeBatch(db);
			batchCount = 0;
		}
	};

	try {
		const [applicantsSnapshot, applicationsSnapshot, educationSnapshot] = await Promise.all([
			getDocs(collection(db, collections.applicants)),
			getDocs(collection(db, collections.applications)),
			getDocs(collection(db, collections.education)),
		]);

		const educationById = new Map<string, DocumentData>();
		for (const eduDoc of educationSnapshot.docs) {
			educationById.set(eduDoc.id, eduDoc.data());
		}

		const educationByApplicant = new Map<string, number>();
		for (const appDoc of applicationsSnapshot.docs) {
			const appData = appDoc.data();
			const applicantId = typeof appData.completedBy === 'string' ? appData.completedBy : null;
			const educationId = typeof appData.education === 'string' ? appData.education : null;
			if (!applicantId || !educationId) continue;

			const year = gradYearFromEducationRecord(educationById.get(educationId));
			if (year !== null) educationByApplicant.set(applicantId, year);
		}

		for (const applicantDoc of applicantsSnapshot.docs) {
			const data = applicantDoc.data();
			const current = data.gradYear;
			if (isCanonicalGradYear(current)) {
				results.skipped++;
				continue;
			}

			const normalized = normalizeGradYear(current) ?? educationByApplicant.get(applicantDoc.id) ?? null;
			if (normalized === null) {
				results.skipped++;
				continue;
			}

			batch.update(doc(db, collections.applicants, applicantDoc.id), { gradYear: normalized });
			batchCount++;
			results.updated++;
			results.updates.push({ id: applicantDoc.id, from: current, to: normalized });
			if (batchCount >= batchSize) await commitBatch();
		}

		await commitBatch();
	} catch (e) {
		results.errors.push(e instanceof Error ? e.message : String(e));
	}

	return results;
};